// EmailManagement.jsx — landing for the Email Management package.
// Lives at /email-management. Phase 1 ships the tab shell + the
// Accounts / Templates / Contacts / Lists / Intents / Campaigns / Inbox
// sub-screens as data-tables backed by the REST routes in
// /api/email/*. Each tab fetches list data on mount, displays it, and
// exposes a thin "New / Edit / Delete" affordance.
//
// Phase 2-6 fill in the connection wizard (IMAP/SMTP), the inbox reader,
// the campaign builder, and the CRM views.
//
// Route is gated by EMAIL_MESSAGE_VIEW in App.jsx. Each per-action
// button additionally checks the appropriate permission so a viewer
// role can browse but not mutate.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import { get, post } from "../../api";
import { ALL_PERMISSIONS } from "constants/permissionsConstants";
import JourneyTimeline from "../CRM/JourneyTimeline";
import VerifiedTick from "../../components/VerifiedTick";
import SystemFilterBar from "../../components/SystemFilterBar";
import { applySystemFilter, EMPTY_SYSTEM_FILTERS } from "../../helpers/systemFilters";
import useBrandList from "../../hooks/useBrandList";
import SignaturesTab from "./SignaturesTab";

const TABS = [
  { key: 'inbox',     label: 'Inbox',     endpoint: '/api/email/messages?direction=inbound', perm: 'EMAIL_MESSAGE_VIEW' },
  { key: 'campaigns', label: 'Campaigns', endpoint: '/api/email/campaigns',                  perm: 'EMAIL_CAMPAIGN_VIEW' },
  { key: 'sequences', label: 'Sequences', endpoint: '/api/email/sequences',                  perm: 'EMAIL_SEQUENCE_VIEW' },
  { key: 'crmsources', label: 'CRM Sources', endpoint: '/api/crm-sources',                    perm: 'CRM_SOURCE_VIEW' },
  // Newsletters live in their own page at /newsletter-management — no
  // duplicate tab here. Keep the entity APIs in /api/email/newsletters/*
  // since the schedule wraps an EmailAccount + EmailContactList.
  { key: 'contacts',  label: 'Contacts',  endpoint: '/api/email/contacts',                   perm: 'EMAIL_CONTACT_VIEW' },
  { key: 'templates', label: 'Templates', endpoint: '/api/email/templates',                  perm: 'EMAIL_TEMPLATE_VIEW' },
  { key: 'lists',     label: 'Lists',     endpoint: '/api/email/lists',                      perm: 'EMAIL_LIST_VIEW' },
  { key: 'intents',   label: 'Intents',   endpoint: '/api/email/intents',                    perm: 'EMAIL_INTENT_VIEW' },
  { key: 'accounts',  label: 'Mailboxes', endpoint: '/api/email/accounts',                   perm: 'EMAIL_ACCOUNT_VIEW' },
  // Owns its own data and its own editor, so it opts out of the shared table
  // renderer below (SELF_CONTAINED) rather than being bent into column shape.
  { key: 'signatures', label: 'Signatures', endpoint: null,                                   perm: 'EMAIL_ACCOUNT_VIEW' },
];

// Tabs that fetch and render themselves. The shared table knows how to draw a
// row of scalars; a signature is a block of HTML with a preview.
const SELF_CONTAINED = new Set(['signatures']);

// Per-tab column hints — drives the table renderer. Keep tiny; Phase 7
// replaces this with real components per entity.
const COLUMNS = {
  inbox:     [['from.address','From'], ['subject','Subject'], ['intentLabel','Intent'], ['receivedAt','Received']],
  // Campaigns surface the full tracking funnel: sent → opened → clicked,
  // with bounces flagged. Open % / Click % are computed below.
  campaigns: [['name','Name'], ['status','Status'], ['scheduledAt','Scheduled'], ['stats.sent','Sent'], ['stats.opened','Opened'], ['stats.clicked','Clicked'], ['__openRate','Open %'], ['__clickRate','Click %'], ['stats.bounced','Bounced'], ['stats.unsubscribed','Unsubs']],
  sequences: [['name','Name'], ['status','Status'], ['__live','Live'], ['__progress','Sent / Total'], ['__mailboxes','Mailboxes'], ['__stepCount','Steps'], ['goal','Goal'], ['updatedAt','Updated']],
  crmsources: [['name','Name'], ['kind','Kind'], ['category','Category'], ['enabled','On'], ['backfillOffset','Pulled'], ['sourceTotal','Total'], ['lastSyncAt','Last sync']],
  contacts:  [['email','Email'], ['firstName','First'], ['lastName','Last'], ['lifecycle','Stage'], ['lastInboundAt','Last inbound']],
  templates: [['name','Name'], ['kind','Kind'], ['listType','Audience'], ['subject','Subject'], ['updatedAt','Updated']],
  lists:     [['name','Name'], ['kind','Kind'], ['memberCount','Members'], ['lastResolvedAt','Resolved']],
  intents:   [['label','Label'], ['autoReplyEnabled','Auto-reply'], ['requireApproval','Approval'], ['minConfidence','Min conf.']],
  accounts:  [['name','Name'], ['fromAddress','From'], ['status','Status'], ['__warmup','Warmup'], ['__marketing','Marketing'], ['__paused','Paused?'], ['lastSyncAt','Last sync']],
};

// CreateModal — generic "+ New X" wizard driven by a per-tab field
// config. Each field declares { key, label, type, required?, options?,
// rows?, help? }. type supports text/email/number/textarea/checkbox/
// select. options can be a static array or async loader (string =>
// endpoint, returns [{value,label}]).
//
// On submit, POSTs to the entity's endpoint with the form payload; on
// success, calls onSaved (caller refreshes the list).
function CreateModal({ title, fields, endpoint, initial = {}, onClose, onSaved }) {
  const [form, setForm] = useState({ ...Object.fromEntries(fields.map((f) => [f.key, f.default ?? (f.type === 'checkbox' ? false : '')])), ...initial });
  const [opts, setOpts] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Load async option lists (mailbox dropdown, template dropdown, etc.)
  useEffect(() => {
    let cancelled = false;
    fields.filter((f) => typeof f.options === 'string').forEach(async (f) => {
      try {
        const r = await get(f.options);
        // Some endpoints return { data: [...] }, others return { brands: [...] }
        // Handle both shapes so the brand dropdown works.
        let items = r && r.data;
        if (!Array.isArray(items)) items = r && r.brands;
        if (!Array.isArray(items)) items = [];
        const list = items.map((x) => ({ value: x._id || x.id, label: f.optionLabel ? f.optionLabel(x) : (x.name || x.label || x.email || x._id) }));
        if (!cancelled) setOpts((p) => ({ ...p, [f.key]: list }));
      } catch (_) { /* leave empty */ }
    });
    return () => { cancelled = true; };
  }, [fields]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    for (const f of fields) {
      if (f.required && (form[f.key] == null || form[f.key] === '')) {
        return setErr(`${f.label} is required`);
      }
    }
    setBusy(true); setErr(null);
    try {
      await post(endpoint, form, false);
      onSaved && onSaved();
    } catch (e) {
      setErr((e && (e.message || (e.response && e.response.data && e.response.data.error))) || 'Save failed');
    } finally { setBusy(false); }
  };

  const renderField = (f) => {
    const id = `cm-${f.key}`;
    const common = { id, value: form[f.key] ?? '', onChange: (e) => set(f.key, f.type === 'number' ? Number(e.target.value) : e.target.value), style: { fontSize: 12, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4, width: '100%' } };
    if (f.type === 'checkbox') return <input id={id} type="checkbox" checked={!!form[f.key]} onChange={(e) => set(f.key, e.target.checked)} />;
    if (f.type === 'textarea') return <textarea {...common} rows={f.rows || 4} placeholder={f.placeholder || ''} />;
    if (f.type === 'select') {
      const list = Array.isArray(f.options) ? f.options : (opts[f.key] || []);
      return (
        <select {...common}>
          <option value="">— pick —</option>
          {list.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
        </select>
      );
    }
    return <input {...common} type={f.type || 'text'} placeholder={f.placeholder || ''} />;
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', width: 640, maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto', borderRadius: 8, padding: 20 }} onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-baseline justify-content-between mb-3">
          <h5 className="mb-0">{title}</h5>
          <button className="acc-btn" onClick={onClose}>Close</button>
        </div>
        {fields.map((f) => (
          <label key={f.key} style={{ display: 'flex', flexDirection: f.type === 'checkbox' ? 'row' : 'column', alignItems: f.type === 'checkbox' ? 'center' : 'stretch', gap: f.type === 'checkbox' ? 8 : 2, fontSize: 11, color: '#525252', marginBottom: 10 }}>
            <span style={{ fontWeight: 500 }}>{f.label}{f.required ? ' *' : ''}</span>
            {renderField(f)}
            {f.help && <small style={{ color: '#888', fontSize: 10 }}>{f.help}</small>}
          </label>
        ))}
        {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <button className="acc-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="acc-btn acc-btn-primary" onClick={submit} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// Per-tab "+ New" configuration. Each entry produces a CreateModal with
// the right fields + endpoint when the user clicks the tab header
// button. Missing entries (inbox, accounts) use their own bespoke
// modals (AddMailboxModal, ComposeModal).
const CREATE_CONFIG = {
  campaigns: {
    title: 'New campaign',
    endpoint: '/api/email/campaigns',
    perm: 'EMAIL_CAMPAIGN_ADD',
    fields: [
      { key: 'name', label: 'Name', required: true, placeholder: 'Q3 newsletter' },
      { key: 'template_id', label: 'Template', type: 'select', required: true, options: '/api/email/templates', optionLabel: (x) => `${x.name} (${x.kind})` },
      { key: 'account_id',  label: 'Send from mailbox', type: 'select', required: true, options: '/api/email/accounts', optionLabel: (x) => `${x.name} <${x.fromAddress}>` },
      { key: 'list_id',     label: 'Audience list', type: 'select', required: true, options: '/api/email/lists', optionLabel: (x) => `${x.name} (${x.memberCount || 0} contacts)` },
      { key: 'subjectOverride', label: 'Subject override (optional)' },
      { key: 'rateLimitPerMinute', label: 'Rate limit / minute', type: 'number', help: '0 = no throttling' },
      { key: 'scheduledAt',  label: 'Schedule for (ISO date-time, blank = draft)', placeholder: '2026-05-15T09:00:00Z' },
    ],
  },
  contacts: {
    title: 'New contact',
    endpoint: '/api/email/contacts',
    perm: 'EMAIL_CONTACT_ADD',
    fields: [
      { key: 'email',        label: 'Email', type: 'email', required: true },
      { key: 'firstName',    label: 'First name' },
      { key: 'lastName',     label: 'Last name' },
      { key: 'company_name', label: 'Company' },
      { key: 'title',        label: 'Title' },
      { key: 'phone',        label: 'Phone' },
      { key: 'lifecycle',    label: 'Lifecycle', type: 'select', options: ['new','engaged','qualified','customer','churned','unsubscribed'].map((v) => ({ value: v, label: v })), default: 'new' },
    ],
  },
  templates: {
    title: 'New template',
    endpoint: '/api/email/templates',
    perm: 'EMAIL_TEMPLATE_ADD',
    fields: [
      { key: 'name',     label: 'Name', required: true },
      // kind=crm + a crmAction value makes this template the LLM's
      // exemplar for the matching CRM action (customer or prospect).
      // Without that pair, the action processor generates from scratch.
      { key: 'kind',     label: 'Kind', type: 'select', options: ['campaign','autoreply','transactional','crm','structure'].map((v) => ({ value: v, label: v })), default: 'campaign' },
      { key: 'listType', label: 'Audience (for kind = structure — which lifecycle this structure governs)', type: 'select', options: ['','cold','warm','unsatisfied','happy','all'].map((v) => ({ value: v, label: v || '(none)' })), default: '' },
      { key: 'crmAction',label: 'CRM action (only when kind = crm)', type: 'select', options: [
          { value: '', label: '— pick action —' },
          { value: 'checkin',           label: 'Customer · check-in' },
          { value: 'testimonial-ask',   label: 'Customer · testimonial request' },
          { value: 'retention',         label: 'Customer · retention (at-risk)' },
          { value: 'support-followup',  label: 'Customer · support follow-up' },
          { value: 'reactivation',      label: 'Customer · reactivation (dormant)' },
          { value: 'intro',             label: 'Prospect · first-touch intro' },
          { value: 'followup',          label: 'Prospect · follow-up' },
          { value: 'drip',              label: 'Prospect · nurturing drip' },
          { value: 'qualifying-ask',    label: 'Prospect · qualifying question' },
          { value: 'close-nudge',       label: 'Prospect · close nudge' },
        ], help: 'When kind=crm, this template becomes the LLM exemplar for the matching action.' },
      { key: 'brand_id', label: 'Brand (optional — scopes the template to one brand)', type: 'select', options: '/brands', optionLabel: (x) => x.name },
      { key: 'subject',  label: 'Subject', placeholder: 'Use {{firstName}} for substitution' },
      { key: 'bodyText', label: 'Body (plain text)', type: 'textarea', rows: 8 },
      { key: 'bodyHtml', label: 'Body (HTML, optional)', type: 'textarea', rows: 5 },
    ],
  },
  lists: {
    title: 'New contact list',
    endpoint: '/api/email/lists',
    perm: 'EMAIL_LIST_ADD',
    fields: [
      { key: 'name',        label: 'Name', required: true },
      { key: 'description', label: 'Description' },
      // brand_id drives the worker's content-pull when this list is the
      // audience for a newsletter / campaign — the LLM uses the linked
      // brand's voice + recent posts/articles to craft outbound content.
      { key: 'brand_id',    label: 'Brand (optional)', type: 'select', options: '/brands', optionLabel: (x) => x.name, help: 'Ties this list to a brand so the AI can pull that brand\'s knowledge when crafting emails.' },
      { key: 'kind',        label: 'Kind', type: 'select', options: [{ value: 'static', label: 'static (manual member list)' }, { value: 'dynamic', label: 'dynamic (rule-based)' }], default: 'static' },
      { key: 'rule',        label: 'Rule JSON (dynamic only)', type: 'textarea', rows: 4, placeholder: '{"lifecycle":"engaged","tags":{"$in":["newsletter"]}}', help: 'Mongo query on EmailContact; company scope auto-injected.' },
    ],
  },
  // Newsletter schedule form moved to NewsletterManagement.jsx — that
  // page has the richer editor with brand picker, contentSources, and
  // recency window controls. EmailManagement no longer owns the entity.
  intents: {
    title: 'New intent rule',
    endpoint: '/api/email/intents',
    perm: 'EMAIL_INTENT_ADD',
    fields: [
      { key: 'label',          label: 'Label', required: true, placeholder: 'pricing_question' },
      { key: 'description',    label: 'Description', type: 'textarea', rows: 3, help: 'Used by the classifier to decide if an inbound matches this intent.' },
      { key: 'autoReplyEnabled', label: 'Auto-reply on match', type: 'checkbox' },
      { key: 'requireApproval',  label: 'Require human approval before sending', type: 'checkbox', default: true },
      { key: 'reply_template_id', label: 'Reply template', type: 'select', options: '/api/email/templates', optionLabel: (x) => `${x.name} (${x.kind})` },
      { key: 'minConfidence',  label: 'Min confidence (0-1)', type: 'number', default: 0.7, help: 'Skip auto-reply if classifier confidence is lower.' },
    ],
  },
};

// CsvImportModal — file picker for the contacts CSV import endpoint.
// Hits POST /api/email/contacts/import as multipart/form-data with
// field `file`. Server-side parses headers; we just upload the bytes.
// ContactActionModal — edit fields / assign-brand / add-to-list for one or a
// selected set of contacts. `action` = { type, ids, contact? }.
function ContactActionModal({ action, onClose, onDone }) {
  const { type, ids, contact } = action;
  const [brands, setBrands] = useState([]);
  const [lists, setLists] = useState([]);
  const [brandId, setBrandId] = useState(contact ? (contact.brand_id || '') : '');
  const [listId, setListId] = useState('');
  const [form, setForm] = useState({
    firstName: contact?.firstName || '', lastName: contact?.lastName || '',
    company_name: contact?.company_name || '', title: contact?.title || '',
    phone: contact?.phone || '', lifecycle: contact?.lifecycle || '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (type === 'assign-brand' || type === 'edit') get('/brands', false).then((r) => setBrands(r?.brands || r?.data || r || [])).catch(() => {});
    if (type === 'assign-list') get('/api/email/lists', false).then((r) => setLists((r?.data || []).filter((l) => l.kind === 'static'))).catch(() => {});
  }, [type]);

  const single = ids.length === 1;
  const titleTxt = { edit: 'Edit contact fields', 'assign-brand': 'Assign to brand', 'assign-list': 'Add to list' }[type];

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      if (type === 'assign-brand') {
        await post('/api/email/contacts/bulk', { ids, action: 'assign-brand', brand_id: brandId || null }, false);
      } else if (type === 'assign-list') {
        if (!listId) throw new Error('Pick a list');
        await post(`/api/email/lists/${listId}/add-contacts`, { contactIds: ids }, false);
      } else if (type === 'edit') {
        if (single) {
          const { putGeneral } = await import('../../api');
          await putGeneral(`/api/email/contacts/${ids[0]}`, form);
        } else {
          // bulk: only send the fields the user actually filled (non-empty)
          const payload = { ids, action: 'edit' };
          Object.entries(form).forEach(([k, v]) => { if (String(v).trim() !== '') payload[k] = v; });
          await post('/api/email/contacts/bulk', payload, false);
        }
      }
      onDone();
    } catch (e) { setErr((e && (e.response?.data?.error || e.message)) || 'Failed'); }
    finally { setBusy(false); }
  };

  const fld = { width: '100%', fontSize: 12, padding: '6px 8px', border: '1px solid #ddd', borderRadius: 4 };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 55, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', width: 460, maxWidth: '92vw', borderRadius: 8, padding: 20 }} onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-baseline justify-content-between mb-2">
          <h5 className="mb-0">{titleTxt} {!single && <span style={{ fontSize: 12, color: '#888' }}>({ids.length} contacts)</span>}</h5>
          <button className="acc-btn" onClick={onClose}>Close</button>
        </div>
        {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
        {type === 'assign-brand' && (
          <label style={{ display: 'block', fontSize: 11, color: '#525252' }}>Brand
            <select value={brandId} onChange={(e) => setBrandId(e.target.value)} style={fld}>
              <option value="">— none (unassign) —</option>
              {brands.map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
            </select>
          </label>
        )}
        {type === 'assign-list' && (
          <label style={{ display: 'block', fontSize: 11, color: '#525252' }}>Static list
            <select value={listId} onChange={(e) => setListId(e.target.value)} style={fld}>
              <option value="">— pick a list —</option>
              {lists.map((l) => <option key={l._id} value={l._id}>{l.name} ({l.memberCount || 0})</option>)}
            </select>
            <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>Dynamic (rule-based) lists fill automatically and aren't shown here.</div>
          </label>
        )}
        {type === 'edit' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {!single && <div style={{ gridColumn: '1 / -1', fontSize: 11, color: '#9ca3af' }}>Only the fields you fill will be applied to all {ids.length} contacts.</div>}
            <label style={{ fontSize: 11, color: '#525252' }}>First<input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} style={fld} /></label>
            <label style={{ fontSize: 11, color: '#525252' }}>Last<input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} style={fld} /></label>
            <label style={{ fontSize: 11, color: '#525252' }}>Company<input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} style={fld} /></label>
            <label style={{ fontSize: 11, color: '#525252' }}>Title<input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} style={fld} /></label>
            <label style={{ fontSize: 11, color: '#525252' }}>Phone<input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} style={fld} /></label>
            <label style={{ fontSize: 11, color: '#525252' }}>Lifecycle
              <select value={form.lifecycle} onChange={(e) => setForm({ ...form, lifecycle: e.target.value })} style={fld}>
                <option value="">— unchanged —</option>
                {['new','engaged','qualified','customer','churned','unsubscribed'].map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
            </label>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button className="acc-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="acc-btn acc-btn-primary" onClick={submit} disabled={busy}>{busy ? 'Saving…' : 'Apply'}</button>
        </div>
      </div>
    </div>
  );
}

function CsvImportModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [brands, setBrands] = useState([]);
  // Batch defaults — applied to every row that doesn't override the
  // column. The user usually imports a CSV scoped to one brand + one
  // type, so we set these once at the modal level instead of forcing
  // them into the CSV.
  const [batchBrandId, setBatchBrandId] = useState('');
  const [batchType, setBatchType] = useState('prospect');

  useEffect(() => {
    (async () => {
      try { const r = await get('/brands'); setBrands((r && r.data) || []); } catch (_) {}
    })();
  }, []);

  const submit = async () => {
    if (!file) return setErr('Pick a CSV file first');
    setBusy(true); setErr(null);
    const fd = new FormData();
    fd.append('file', file);
    if (batchBrandId) fd.append('brand_id', batchBrandId);
    fd.append('type', batchType);
    try {
      // post() from src/api wraps axios — pass FormData directly + let
      // the browser set the multipart boundary header.
      const r = await post('/api/email/contacts/import', fd, false);
      setResult((r && r.data) || null);
    } catch (e) { setErr((e && e.message) || 'Import failed'); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', width: 600, maxWidth: '92vw', borderRadius: 8, padding: 20 }} onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-baseline justify-content-between mb-3">
          <h5 className="mb-0">Import contacts from CSV</h5>
          <button className="acc-btn" onClick={onClose}>Close</button>
        </div>
        <div className="em-stub mb-2">
          Required header: <code>email</code>. Optional: <code>firstName, lastName, company_name, title, phone, tags, lifecycle, brand_id, type</code>. Tags column is comma-separated. Upserts by (company, email).
        </div>

        <div style={{ background: '#fafbfc', padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Defaults for this batch</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={{ fontSize: 11 }}>Brand (AI uses its voice for these contacts)
              <select className="form-control" value={batchBrandId} onChange={(e) => setBatchBrandId(e.target.value)} style={{ fontSize: 12 }}>
                <option value="">— pick brand (optional) —</option>
                {brands.map((b) => (<option key={b._id} value={b._id}>{b.name}</option>))}
              </select>
            </label>
            <label style={{ fontSize: 11 }}>Default type (rows can override with `type` column)
              <select className="form-control" value={batchType} onChange={(e) => setBatchType(e.target.value)} style={{ fontSize: 12 }}>
                <option value="prospect">Prospect (default — funnel goal: convert)</option>
                <option value="customer">Customer (already paying — funnel goal: retain)</option>
              </select>
            </label>
          </div>
          <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>
            Without a brand, AI falls back to the company's default brand voice.
          </div>
        </div>

        <input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)} style={{ fontSize: 12, marginBottom: 12 }} />
        {result && (
          <div style={{ padding: 8, fontSize: 12, background: '#e7f5ec', color: '#1e6b3e', borderRadius: 4, marginBottom: 10 }}>
            ✓ {result.upserted} upserted · {result.skipped} skipped · {result.errors?.length || 0} errors
            {result.errors?.length > 0 && (
              <ul style={{ margin: '6px 0 0 16px', padding: 0, fontSize: 11, color: '#a71d2a' }}>
                {result.errors.slice(0, 5).map((er, i) => (<li key={i}>{er.email}: {er.error}</li>))}
              </ul>
            )}
          </div>
        )}
        {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          {result ? (
            <button className="acc-btn acc-btn-primary" onClick={() => { onImported(); }}>Done</button>
          ) : (
            <>
              <button className="acc-btn" onClick={onClose} disabled={busy}>Cancel</button>
              <button className="acc-btn acc-btn-primary" onClick={submit} disabled={busy || !file}>{busy ? 'Importing…' : 'Import'}</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ComposeModal — write a brand new outbound email from scratch. Phase 7
// addition (P3 only handled REPLIES). The send button calls /messages/send
// which emits EMAIL_SEND_REQUESTED → the worker mails it.
function ComposeModal({ onClose, onSent }) {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ account_id: '', to: '', subject: '', bodyText: '' });
  const [attachments, setAttachments] = useState([]); // [{filename, contentType, sizeBytes, storageKey, uploading}]
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => { (async () => {
    try { const r = await get('/api/email/accounts'); setAccounts((r && r.data) || []); }
    catch (e) { setErr((e && e.message) || 'Failed to load mailboxes'); }
  })(); }, []);

  // For each picked file: 1) ask backend for a presigned PUT URL,
  // 2) PUT the file directly to R2, 3) record the storageKey so the
  // /send call can reference it. Each file uploads independently;
  // failure on one doesn't block the others.
  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setErr(null);
    const newRows = files.map((f) => ({ filename: f.name, contentType: f.type || 'application/octet-stream', sizeBytes: f.size, storageKey: '', uploading: true, _file: f }));
    setAttachments((prev) => [...prev, ...newRows]);

    await Promise.all(newRows.map(async (row) => {
      try {
        const r = await post('/api/email/messages/upload-url', { filename: row.filename, contentType: row.contentType }, false);
        const { uploadUrl, storageKey } = (r && r.data) || {};
        if (!uploadUrl) throw new Error('no upload URL returned');
        const put = await fetch(uploadUrl, { method: 'PUT', body: row._file, headers: { 'Content-Type': row.contentType } });
        if (!put.ok) throw new Error(`R2 upload ${put.status}`);
        setAttachments((prev) => prev.map((a) => a === row ? { filename: row.filename, contentType: row.contentType, sizeBytes: row.sizeBytes, storageKey, uploading: false } : a));
      } catch (uploadErr) {
        setAttachments((prev) => prev.filter((a) => a !== row));
        setErr(`${row.filename}: ${uploadErr.message || 'upload failed'}`);
      }
    }));
  };

  const removeAttachment = (idx) => setAttachments((prev) => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!form.account_id || !form.to || !form.subject) return setErr('From mailbox, To, and Subject are required');
    if (attachments.some((a) => a.uploading)) return setErr('Wait for attachments to finish uploading');
    setBusy(true); setErr(null);
    try {
      const tos = String(form.to).split(',').map((s) => s.trim()).filter(Boolean).map((address) => ({ address }));
      const payload = {
        account_id: form.account_id, to: tos, subject: form.subject, bodyText: form.bodyText,
        attachments: attachments.filter((a) => a.storageKey).map((a) => ({ filename: a.filename, contentType: a.contentType, sizeBytes: a.sizeBytes, storageKey: a.storageKey })),
      };
      await post('/api/email/messages/send', payload, false);
      onSent && onSent();
    } catch (e) { setErr((e && e.message) || 'Send failed'); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', width: 640, maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto', borderRadius: 8, padding: 20 }} onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-baseline justify-content-between mb-3">
          <h5 className="mb-0">New email</h5>
          <button className="acc-btn" onClick={onClose}>Close</button>
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, color: '#525252', gap: 2, marginBottom: 10 }}>
          <span>From mailbox *</span>
          <select value={form.account_id} onChange={(e) => { const _v = e.target.value; setForm((f) => ({ ...f, account_id: _v })); }}
                  style={{ fontSize: 12, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4 }}>
            <option value="">— pick —</option>
            {accounts.map((a) => (<option key={a._id} value={a._id}>{a.name} &lt;{a.fromAddress}&gt;</option>))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, color: '#525252', gap: 2, marginBottom: 10 }}>
          <span>To * (comma-separate for multiple)</span>
          <input value={form.to} onChange={(e) => { const _v = e.target.value; setForm((f) => ({ ...f, to: _v })); }} style={{ fontSize: 12, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4 }} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, color: '#525252', gap: 2, marginBottom: 10 }}>
          <span>Subject *</span>
          <input value={form.subject} onChange={(e) => { const _v = e.target.value; setForm((f) => ({ ...f, subject: _v })); }} style={{ fontSize: 12, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4 }} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, color: '#525252', gap: 2, marginBottom: 10 }}>
          <span>Body</span>
          <textarea value={form.bodyText} onChange={(e) => { const _v = e.target.value; setForm((f) => ({ ...f, bodyText: _v })); }} rows={10} style={{ fontSize: 13, padding: 8, border: '1px solid #ddd', borderRadius: 4 }} />
        </label>
        <div style={{ marginBottom: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#525252' }}>
            <input type="file" multiple onChange={onPickFiles} style={{ fontSize: 12 }} />
            <span style={{ fontSize: 11, color: '#888' }}>Upload one or more attachments (R2-backed)</span>
          </label>
          {attachments.length > 0 && (
            <ul style={{ listStyle: 'none', padding: 0, margin: '6px 0 0' }}>
              {attachments.map((a, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '3px 6px', background: '#f7f9fc', borderRadius: 4, marginBottom: 2 }}>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.filename}</span>
                  <span style={{ color: '#888', fontSize: 11 }}>{(a.sizeBytes / 1024).toFixed(1)} KB</span>
                  {a.uploading
                    ? <span style={{ color: '#0F62FE', fontSize: 11 }}>uploading…</span>
                    : <button className="acc-btn" type="button" onClick={() => removeAttachment(i)} style={{ padding: '2px 8px' }}>×</button>}
                </li>
              ))}
            </ul>
          )}
        </div>
        {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="acc-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="acc-btn acc-btn-primary" onClick={submit} disabled={busy}>{busy ? 'Sending…' : 'Send'}</button>
        </div>
      </div>
    </div>
  );
}

// AddMailboxModal — Phase 2 connection wizard. Collects IMAP + SMTP
// host/port/user/password, lets you "Test" both legs before saving.
// On test, we POST against the live worker via /api/email/accounts/:id/test;
// the backend probe is timed-bounded (5s per side) and returns per-leg
// {ok, error}. On save, the row's status flips to 'connected' so the
// worker's poller starts ingesting on the next 5min tick.
function AddMailboxModal({ onClose, onSaved, initial }) {
  const [form, setForm] = useState({
    name: '', fromName: '', fromAddress: '', brand_id: '',
    provider: 'imap', // 'imap' (password) | 'm365' (OAuth)
    tlsRejectUnauthorized: true, useRelay: false,
    imapHost: '', imapPort: 993, imapUser: '', imapPassword: '', imapTls: true,
    smtpHost: '', smtpPort: 587, smtpUser: '', smtpPassword: '', smtpTls: true,
    autoReplyEnabled: false,
    ...(initial || {}),
  });
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);
  const [brands, setBrands] = useState([]);
  const editingId = initial && initial._id;
  const isM365 = form.provider === 'm365';

  useEffect(() => { (async () => { try { const r = await get('/brands'); setBrands((r && r.data) || r || []); } catch (_) {} })(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Switching to Microsoft 365 pins the office365 endpoints + OAuth.
  const onProvider = (p) => setForm((f) => ({
    ...f, provider: p,
    ...(p === 'm365'
      ? { authType: 'oauth', imapHost: 'outlook.office365.com', imapPort: 993, imapTls: true, smtpHost: 'smtp.office365.com', smtpPort: 587, smtpTls: true }
      : { authType: 'password' }),
  }));

  // Save (creating if needed) then kick the Microsoft consent flow.
  const connectMicrosoft = async () => {
    setError(null); setBusy(true);
    try {
      let id = editingId;
      if (!id) { const r = await post('/api/email/accounts', { ...form, authType: 'oauth', provider: 'm365' }, false); id = r && r.data && r.data._id; }
      if (!id) throw new Error('could not save connector');
      const r = await get(`/api/email/oauth/microsoft/start?accountId=${id}`);
      const url = r && r.data && r.data.url;
      if (url) window.location.href = url; else throw new Error('no consent URL returned');
    } catch (e) { setError((e && e.message) || 'Microsoft connect failed'); setBusy(false); }
  };

  // For convenience: when the user fills imapUser, auto-fill smtpUser +
  // fromAddress if they haven't been edited yet. Same trick most clients use.
  const onImapUser = (v) => {
    setForm((f) => ({
      ...f, imapUser: v,
      smtpUser:    f.smtpUser    || v,
      fromAddress: f.fromAddress || v,
    }));
  };

  const submitTest = async () => {
    setError(null); setTestResult(null); setBusy(true);
    try {
      // Test against a not-yet-created account by POSTing to the
      // wildcard `_new` id; the backend accepts overrides in the body
      // for pre-save validation. (Routes handle the lookup gracefully.)
      const r = await post('/api/email/accounts/_new/test', form, false);
      setTestResult(r && r.data ? r.data : r);
    } catch (e) { setError((e && e.message) || 'Test failed'); }
    finally { setBusy(false); }
  };

  const submitSave = async () => {
    setError(null); setBusy(true);
    try {
      const r = await post('/api/email/accounts', form, false);
      const id = r && r.data && r.data._id;
      if (id) {
        // Run the saved-cred test once so status flips to 'connected'.
        try { await post(`/api/email/accounts/${id}/test`, {}, false); } catch (_) {}
      }
      onSaved && onSaved();
    } catch (e) { setError((e && e.message) || 'Save failed'); }
    finally { setBusy(false); }
  };

  const field = (label, k, type = 'text', extra = {}) => (
    <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, color: '#525252', gap: 2 }}>
      <span>{label}</span>
      <input type={type} value={form[k]} onChange={(e) => set(k, type === 'number' ? Number(e.target.value) : e.target.value)}
             style={{ fontSize: 12, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4 }} {...extra} />
    </label>
  );

  const both = testResult && testResult.imap?.ok && testResult.smtp?.ok;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
         onClick={onClose}>
      <div style={{ background: '#fff', width: 720, maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto', borderRadius: 8, padding: 20 }}
           onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-baseline justify-content-between mb-2">
          <h5 className="mb-0">Connect mailbox</h5>
          <button className="acc-btn" onClick={onClose}>Close</button>
        </div>
        <p className="em-stub mb-3">IMAP for receiving + SMTP for sending. Passwords are encrypted at rest (AES-256-GCM). The worker polls every 5 min once connected.</p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          {field('Mailbox name', 'name')}
          {field('From address', 'fromAddress', 'email', { placeholder: 'support@yourdomain.com' })}
          {field('From name (display)', 'fromName', 'text', { placeholder: 'Your Brand Support' })}
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, color: '#525252', gap: 2 }}>
            <span>Brand</span>
            <select value={form.brand_id || ''} onChange={(e) => set('brand_id', e.target.value)} style={{ fontSize: 12, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4 }}>
              <option value="">— unassigned —</option>
              {brands.map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
            </select>
          </label>
        </div>

        {/* Connector type + options */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10, fontSize: 12 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 2, color: '#525252' }}>
            <span>Connector type</span>
            <select value={form.provider} onChange={(e) => onProvider(e.target.value)} style={{ fontSize: 12, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4 }}>
              <option value="imap">Password (IMAP/SMTP)</option>
              <option value="m365">Microsoft 365 (OAuth)</option>
            </select>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }} title="Accept a self-signed mail-server certificate">
            <input type="checkbox" checked={form.tlsRejectUnauthorized === false} onChange={(e) => set('tlsRejectUnauthorized', !e.target.checked)} /> Allow self-signed TLS
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }} title="Send via the Prigmar relay instead of this server's SMTP">
            <input type="checkbox" checked={!!form.useRelay} onChange={(e) => set('useRelay', e.target.checked)} /> Send via Prigmar relay
          </label>
        </div>

        {isM365 && (
          <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 6, padding: 10, marginBottom: 10, fontSize: 12, color: '#15172b' }}>
            Microsoft 365 uses OAuth — no password needed. Save (if new) then click <strong>Connect Microsoft</strong> to authorize <code>{form.fromAddress || 'the mailbox'}</code>. Endpoints are pinned to outlook.office365.com / smtp.office365.com.
          </div>
        )}

        {!isM365 && (<>
        <div style={{ background: '#f7f9fc', border: '1px solid #e5e5e5', borderRadius: 6, padding: 10, marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>IMAP (inbound)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr 2fr 1fr', gap: 8 }}>
            {field('Host', 'imapHost', 'text', { placeholder: 'imap.example.com' })}
            {field('Port', 'imapPort', 'number')}
            <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, color: '#525252', gap: 2 }}>
              <span>User</span>
              <input value={form.imapUser} onChange={(e) => onImapUser(e.target.value)}
                     style={{ fontSize: 12, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4 }} />
            </label>
            {field('Password', 'imapPassword', 'password')}
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#525252' }}>
              <input type="checkbox" checked={form.imapTls} onChange={(e) => set('imapTls', e.target.checked)} /> TLS
            </label>
          </div>
        </div>

        <div style={{ background: '#f7f9fc', border: '1px solid #e5e5e5', borderRadius: 6, padding: 10, marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>SMTP (outbound)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr 2fr 1fr', gap: 8 }}>
            {field('Host', 'smtpHost', 'text', { placeholder: 'smtp.example.com' })}
            {field('Port', 'smtpPort', 'number')}
            {field('User', 'smtpUser')}
            {field('Password', 'smtpPassword', 'password')}
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#525252' }}>
              <input type="checkbox" checked={form.smtpTls} onChange={(e) => set('smtpTls', e.target.checked)} /> TLS
            </label>
          </div>
        </div>

        </>)}

        {testResult && (
          <div style={{ padding: 8, fontSize: 12, background: both ? '#e7f5ec' : '#fdecea', color: both ? '#1e6b3e' : '#a71d2a', borderRadius: 4, marginBottom: 10 }}>
            <div>IMAP: {testResult.imap?.ok ? '✓ ok' : '✗ ' + (testResult.imap?.error || 'failed')}</div>
            <div>SMTP: {testResult.smtp?.ok ? '✓ ok' : '✗ ' + (testResult.smtp?.error || 'failed')}</div>
          </div>
        )}
        {error && <div className="alert alert-warning" style={{ fontSize: 13 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          {isM365 ? (
            <button className="acc-btn acc-btn-primary" disabled={busy || !form.fromAddress} onClick={connectMicrosoft}>{busy ? 'Connecting…' : (editingId ? 'Connect Microsoft' : 'Save + Connect Microsoft')}</button>
          ) : (<>
            <button className="acc-btn" disabled={busy} onClick={submitTest}>{busy ? 'Testing…' : 'Test connection'}</button>
            <button className="acc-btn acc-btn-primary" disabled={busy || !form.name || !form.fromAddress || !form.imapHost || !form.smtpHost} onClick={submitSave}>{busy ? 'Saving…' : 'Save mailbox'}</button>
          </>)}
        </div>
      </div>
    </div>
  );
}

// Reusable right-side drawer shell. Lets all three drill-ins (message,
// contact, campaign) share the same layout + close behaviour without
// each re-implementing the overlay.
function SidePanel({ title, onClose, children, wide }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'stretch', justifyContent: 'flex-end' }}
         onClick={onClose}>
      <div style={{ background: '#fff', width: wide ? 1040 : 720, maxWidth: '96vw', maxHeight: '100vh', overflowY: 'auto', padding: 20 }} onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-baseline justify-content-between mb-2">
          <h5 className="mb-0">{title}</h5>
          <button className="acc-btn" onClick={onClose}>Close</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ContactActivityPanel — contact row click. Loads /contacts/:id/activity
// which returns {contact, messages} (last 100, in+out, newest-first).
// Renders a profile header + a timeline of inbound/outbound messages,
// each clickable to open the underlying MessageDetailPanel.
function ContactActivityPanel({ id, onClose, onPickMessage }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBusy(true); setErr(null);
      try {
        const r = await get(`/api/email/contacts/${id}/activity`);
        if (!cancelled) setData((r && r.data) || null);
      } catch (e) { if (!cancelled) setErr((e && e.message) || 'Failed to load'); }
      finally { if (!cancelled) setBusy(false); }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const c = data?.contact;
  return (
    <SidePanel title={c ? (c.firstName || c.lastName ? `${c.firstName || ''} ${c.lastName || ''}`.trim() : c.email) : 'Loading…'} onClose={onClose}>
      {busy && !data && <div className="em-empty">Loading…</div>}
      {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
      {c && (
        <>
          <div style={{ fontSize: 12, color: '#525252', marginBottom: 12 }}>
            <div><strong>Email:</strong> {c.email}<VerifiedTick status={c.sendVetStatus} />
              {c.sendVetStatus === 'rejected' && <span title={c.sendVetEvidence || 'failed vetting'} style={{ marginLeft: 6, fontSize: 10, color: '#a71d2a', border: '1px solid #f3c2c2', borderRadius: 6, padding: '0 5px' }}>spam-trap</span>}
            </div>
            {c.company_name && <div><strong>Company:</strong> {c.company_name}</div>}
            {c.title && <div><strong>Title:</strong> {c.title}</div>}
            {c.phone && <div><strong>Phone:</strong> {c.phone}</div>}
            <div style={{ marginTop: 4 }}>
              <span style={{ background: '#eef2ff', padding: '2px 8px', borderRadius: 10, fontSize: 11 }}>{c.lifecycle}</span>
              {c.unsubscribed && <span style={{ background: '#fdecea', color: '#a71d2a', padding: '2px 8px', borderRadius: 10, fontSize: 11, marginLeft: 4 }}>unsubscribed</span>}
              {c.bounced && <span style={{ background: '#fff4cc', color: '#856404', padding: '2px 8px', borderRadius: 10, fontSize: 11, marginLeft: 4 }}>bounced</span>}
              {(c.tags || []).map((t) => (<span key={t} style={{ background: '#f1f1f1', padding: '2px 8px', borderRadius: 10, fontSize: 11, marginLeft: 4 }}>{t}</span>))}
            </div>
          </div>
          <h6 className="mb-2">User journey</h6>
          <div style={{ maxHeight: '34vh', overflow: 'auto', background: '#fff', border: '1px solid #eef0f3', borderRadius: 6, padding: '12px 10px', marginBottom: 14 }}>
            <JourneyTimeline contactId={id} />
          </div>
          <h6 className="mb-2">Activity ({(data.messages || []).length})</h6>
          {(data.messages || []).length === 0 && <div className="em-empty">No emails yet.</div>}
          {(data.messages || []).map((m) => (
            <div key={m._id}
                 onClick={() => { onPickMessage && onPickMessage(m._id); onClose(); }}
                 style={{ display: 'flex', gap: 8, padding: '6px 8px', borderBottom: '1px solid #f4f4f4', fontSize: 12, cursor: 'pointer' }}>
              <span style={{ width: 70, fontWeight: 500, color: m.direction === 'inbound' ? '#0F62FE' : '#1e6b3e' }}>
                {m.direction === 'inbound' ? '← in' : '→ out'}
              </span>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.subject}>{m.subject || '(no subject)'}</span>
              {m.intentLabel && <span style={{ background: '#eef2ff', padding: '0 6px', borderRadius: 8, fontSize: 10 }}>{m.intentLabel}</span>}
              <span style={{ color: '#888', fontSize: 11 }}>{fmtCell(m.receivedAt || m.sentAt)}</span>
            </div>
          ))}
        </>
      )}
    </SidePanel>
  );
}

// AccountHealthPanel — mailbox row click. Today's send budget,
// warmup-day pill, DNS health (SPF/DKIM/DMARC), bounce/complaint
// rates, sendingPaused indicator + Resume button. Cap input lets
// the operator clamp below the warmup ramp if they want to ramp
// slower than 2x/day.
function AccountHealthPanel({ id, onClose, onChanged }) {
  const [account, setAccount] = useState(null);
  const [budget, setBudget] = useState(null);
  const [dns, setDns] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [savingCap, setSavingCap] = useState(false);
  const [capInput, setCapInput] = useState('');

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const list = await get('/api/email/accounts');
      const me = ((list && list.data) || []).find((a) => String(a._id) === String(id));
      setAccount(me || null);
      setCapInput(me && me.dailySendCap ? String(me.dailySendCap) : '');
      const b = await get(`/api/email/accounts/${id}/budget`);
      setBudget((b && b.data) || null);
    } catch (e) { setError((e && e.message) || 'Load failed'); }
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);
  // Budget changes minute-to-minute when sends fire; auto-poll while open.
  useEffect(() => {
    const t = setInterval(() => {
      get(`/api/email/accounts/${id}/budget`)
        .then((b) => setBudget((b && b.data) || null))
        .catch(() => {});
    }, 8000);
    return () => clearInterval(t);
  }, [id]);

  const runDnsCheck = async () => {
    setBusy(true); setError(null);
    try {
      const r = await get(`/api/email/accounts/${id}/dns-check`);
      setDns((r && r.data) || null);
    } catch (e) { setError((e && e.message) || 'DNS check failed'); }
    finally { setBusy(false); }
  };

  const resumeSending = async () => {
    if (!window.confirm('Resume sending? Make sure you\'ve addressed the bounce / complaint source first.')) return;
    setBusy(true); setError(null);
    try {
      await post(`/api/email/accounts/${id}/resume-sending`, {}, false);
      await refresh();
      onChanged && onChanged();
    } catch (e) { setError((e && e.message) || 'Resume failed'); }
    finally { setBusy(false); }
  };

  // Time-boxed cooldown for THIS mailbox — pause sends for N hours, auto-resumes.
  const setCooldown = async (hours) => {
    const label = hours === 0 ? 'lift the cooldown now'
      : `pause this mailbox for ${hours >= 24 ? (hours / 24) + ' day(s)' : hours + ' hour(s)'} (it resumes automatically)`;
    if (!window.confirm(`Cooldown: ${label}?`)) return;
    setBusy(true); setError(null);
    try {
      await post(`/api/email/accounts/${id}/cooldown`, hours === 0 ? { clear: true } : { hours, reason: 'manual cooldown' }, false);
      await refresh();
      onChanged && onChanged();
    } catch (e) { setError((e && e.message) || 'Cooldown failed'); }
    finally { setBusy(false); }
  };

  // Permanently exclude / re-include THIS mailbox as a MARKETING sender
  // (campaigns + sequences + newsletters). 1:1 replies keep working.
  const toggleMarketingSender = async (disabled) => {
    if (!window.confirm(disabled
      ? 'Disable this mailbox as a marketing sender? It will be removed from sender auto-selection and blocked from all campaign/sequence sends (1:1 replies still work).'
      : 'Re-enable this mailbox as a marketing sender?')) return;
    setBusy(true); setError(null);
    try {
      await post(`/api/email/accounts/${id}/marketing-sender`, { disabled, reason: disabled ? 'disabled as marketing sender' : '' }, false);
      await refresh();
      onChanged && onChanged();
    } catch (e) { setError((e && e.message) || 'Toggle failed'); }
    finally { setBusy(false); }
  };

  const saveCap = async () => {
    setSavingCap(true); setError(null);
    try {
      const { putGeneral } = await import('../../api');
      const v = capInput ? Number(capInput) : null;
      await putGeneral(`/api/email/accounts/${id}`, { dailySendCap: v });
      await refresh();
      onChanged && onChanged();
    } catch (e) { setError((e && e.message) || 'Save failed'); }
    finally { setSavingCap(false); }
  };

  const pill = (label, color, bg) => (
    <span style={{ background: bg, color, padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>{label}</span>
  );

  if (!account) {
    return (
      <SidePanel title="Mailbox health" onClose={onClose}>
        {error ? <div className="alert alert-warning" style={{ fontSize: 13 }}>{error}</div> : <div style={{ padding: 16, fontSize: 12, color: '#888' }}>Loading…</div>}
      </SidePanel>
    );
  }

  return (
    <SidePanel title={account.name + ' — health'} onClose={onClose}>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>{account.fromAddress}</div>
      {error && <div className="alert alert-warning" style={{ fontSize: 13 }}>{error}</div>}

      {/* ── Today's budget ───────────────────────────────────────────── */}
      <div style={{ background: '#fafbfc', borderRadius: 6, padding: 12, marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Today's send budget</div>
        {budget ? (
          <>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#15172b' }}>{budget.used}<span style={{ fontSize: 14, color: '#888' }}> / {budget.cap}</span></div>
              <div style={{ fontSize: 12, color: budget.remaining > 0 ? '#1e6b3e' : '#a71d2a' }}>
                {budget.remaining > 0 ? `${budget.remaining} left today` : 'cap reached — sends defer to tomorrow'}
              </div>
            </div>
            <div style={{ height: 6, background: '#e5e5e5', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: Math.min(100, (budget.used / Math.max(1, budget.cap)) * 100) + '%',
                background: budget.used >= budget.cap ? '#a71d2a' : budget.used / budget.cap > 0.8 ? '#d97706' : '#1e6b3e',
              }} />
            </div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>
              Warmup {pill('day ' + budget.warmupDay, '#15172b', '#eef2ff')} · ramp ceiling today: {budget.rampCap} sends
            </div>
          </>
        ) : <div style={{ fontSize: 12, color: '#888' }}>Loading…</div>}
      </div>

      {/* ── Cap override ─────────────────────────────────────────────── */}
      <div style={{ background: '#fafbfc', borderRadius: 6, padding: 12, marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Daily cap override</div>
        <div style={{ fontSize: 12, color: '#525252', marginBottom: 6 }}>
          Clamp below the warmup ramp if you want to ramp slower. Leave blank to follow the ramp.
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="number" min="1" value={capInput} onChange={(e) => setCapInput(e.target.value)} placeholder="e.g. 20" className="form-control" style={{ width: 120, fontSize: 13 }} />
          <button className="acc-btn acc-btn-primary" onClick={saveCap} disabled={savingCap}>{savingCap ? 'Saving…' : 'Save cap'}</button>
        </div>
      </div>

      {/* ── Sending paused ───────────────────────────────────────────── */}
      {account.sendingPaused && (
        <div style={{ background: '#fdecea', border: '1px solid #f5c6cb', borderRadius: 6, padding: 12, marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#a71d2a', marginBottom: 4 }}>⚠ Sending paused</div>
          <div style={{ fontSize: 11, color: '#a71d2a', marginBottom: 8 }}>{account.sendingPausedReason || 'Auto-paused.'}</div>
          <button className="acc-btn acc-btn-primary" onClick={resumeSending} disabled={busy}>{busy ? 'Resuming…' : 'Resume sending'}</button>
        </div>
      )}

      {/* ── Cooldown (time-boxed pause for THIS mailbox) ─────────────── */}
      <div style={{ background: '#fff8e6', border: '1px solid #ffe0a3', borderRadius: 6, padding: 12, marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#856404', marginBottom: 6 }}>Cooldown</div>
        {budget && budget.cooldownUntil ? (
          <>
            <div style={{ fontSize: 11, color: '#856404', marginBottom: 8 }}>
              ⏸ On cooldown until <b>{new Date(budget.cooldownUntil).toLocaleString()}</b>{budget.cooldownReason ? ` — ${budget.cooldownReason}` : ''}. Sends resume automatically.
            </div>
            <button className="acc-btn" onClick={() => setCooldown(0)} disabled={busy}>Lift cooldown now</button>
          </>
        ) : (
          <>
            <div style={{ fontSize: 11, color: '#856404', marginBottom: 8 }}>
              Pause just this mailbox for a set time (it auto-resumes). Use it to cool a box off after a provider flag / restriction — the pool's other mailboxes keep sending.
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[['1h', 1], ['6h', 6], ['24h', 24], ['3d', 72], ['7d', 168]].map(([lbl, h]) => (
                <button key={h} className="acc-btn" onClick={() => setCooldown(h)} disabled={busy}>{lbl}</button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── Marketing sender (permanent exclusion) ──────────────────── */}
      <div style={{ background: account.marketingSenderDisabled ? '#fdecea' : '#f2f8f4', border: `1px solid ${account.marketingSenderDisabled ? '#f5c6cb' : '#cde9d6'}`, borderRadius: 6, padding: 12, marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: account.marketingSenderDisabled ? '#a71d2a' : '#1e6b3e', marginBottom: 6 }}>
          {account.marketingSenderDisabled ? '⛔ Marketing sender: OFF' : 'Marketing sender: on'}
        </div>
        <div style={{ fontSize: 11, color: '#555', marginBottom: 8 }}>
          {account.marketingSenderDisabled
            ? <>Excluded from campaigns + sequences + newsletters — <b>not used by any sequence</b> as a sender, and any queued marketing send from it is blocked. 1:1 replies still work.{account.marketingSenderDisabledReason ? ` — ${account.marketingSenderDisabledReason}` : ''}</>
            : 'This mailbox can be auto-selected + used to send marketing (campaigns/sequences/newsletters).'}
        </div>
        <button className="acc-btn" onClick={() => toggleMarketingSender(!account.marketingSenderDisabled)} disabled={busy}>
          {busy ? 'Saving…' : (account.marketingSenderDisabled ? 'Re-enable as marketing sender' : 'Disable as marketing sender')}
        </button>
      </div>

      {/* ── Deliverability rates ─────────────────────────────────────── */}
      {budget && (
        <div style={{ background: '#fafbfc', borderRadius: 6, padding: 12, marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Deliverability (7-day window)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, fontSize: 12 }}>
            <div>
              <div style={{ color: '#888' }}>Sent</div>
              <div style={{ fontSize: 18, fontWeight: 600 }}>{budget.sentLast7d}</div>
            </div>
            <div>
              <div style={{ color: '#888' }}>Bounces</div>
              <div style={{ fontSize: 18, fontWeight: 600, color: budget.bounceRate > 0.05 ? '#a71d2a' : '#15172b' }}>
                {budget.bouncesLast7d} <span style={{ fontSize: 11, color: '#888' }}>({(budget.bounceRate * 100).toFixed(1)}%)</span>
              </div>
            </div>
            <div>
              <div style={{ color: '#888' }}>Complaints</div>
              <div style={{ fontSize: 18, fontWeight: 600, color: budget.complaintRate > 0.001 ? '#a71d2a' : '#15172b' }}>
                {budget.complaintsLast7d} <span style={{ fontSize: 11, color: '#888' }}>({(budget.complaintRate * 100).toFixed(2)}%)</span>
              </div>
            </div>
          </div>
          <div style={{ fontSize: 10, color: '#888', marginTop: 6 }}>
            Auto-pause triggers at 5% bounce or 0.1% complaint (after ≥50 sends).
          </div>
        </div>
      )}

      {/* ── DNS health ───────────────────────────────────────────────── */}
      <div style={{ background: '#fafbfc', borderRadius: 6, padding: 12, marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>DNS health</div>
          <button className="acc-btn" style={{ fontSize: 11, padding: '2px 10px' }} onClick={runDnsCheck} disabled={busy}>{busy ? 'Checking…' : (dns ? 'Re-check' : 'Run check')}</button>
        </div>
        {!dns && <div style={{ fontSize: 12, color: '#888' }}>Click Run check to resolve SPF / DKIM / DMARC for {account.fromAddress?.split('@')[1] || 'your domain'}.</div>}
        {dns && (
          <div style={{ fontSize: 12 }}>
            <div style={{ marginBottom: 4 }}>
              SPF: {dns.spf?.ok ? pill('✓ present', '#1e6b3e', '#e7f5ec') : pill('✗ ' + (dns.spf?.reason || 'missing'), '#a71d2a', '#fdecea')}
              {dns.spf?.warning && <div style={{ fontSize: 11, color: '#856404', marginTop: 2 }}>⚠ {dns.spf.warning}</div>}
            </div>
            <div style={{ marginBottom: 4 }}>
              DKIM: {dns.dkim?.ok
                ? pill('✓ ' + dns.dkim.selector, '#1e6b3e', '#e7f5ec')
                : pill('✗ no selector found', '#a71d2a', '#fdecea')}
              {!dns.dkim?.ok && <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>Probed common selectors. Provide your custom selector in your DNS provider's panel and re-check.</div>}
            </div>
            <div>
              DMARC: {dns.dmarc?.ok
                ? pill('✓ p=' + (dns.dmarc.policy || '?'), dns.dmarc.enforcing ? '#1e6b3e' : '#856404', dns.dmarc.enforcing ? '#e7f5ec' : '#fff4cc')
                : pill('✗ ' + (dns.dmarc?.reason || 'missing'), '#a71d2a', '#fdecea')}
              {dns.dmarc?.warning && <div style={{ fontSize: 11, color: '#856404', marginTop: 2 }}>⚠ {dns.dmarc.warning}</div>}
            </div>
          </div>
        )}
      </div>
    </SidePanel>
  );
}

// CampaignDetailPanel — campaign row click. Loads the campaign doc +
// recent messages by campaign_id (P7 filter added on /messages?campaign_id=).
// Renders progress bars for the standard funnel stages + recent send
// rows so the user can spot bounces / opens / clicks in real time.
function CampaignDetailPanel({ id, onClose, onPickMessage }) {
  const [campaign, setCampaign] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      const [cRes, mRes] = await Promise.all([
        get('/api/email/campaigns').catch(() => ({ data: [] })),  // no GET /:id endpoint — use list + filter
        get(`/api/email/messages?campaign_id=${id}&limit=100`),
      ]);
      const list = (cRes && cRes.data) || [];
      setCampaign(list.find((c) => c._id === id) || null);
      setMsgs(((mRes && mRes.data) || []));
    } catch (e) { setErr((e && e.message) || 'Failed to load'); }
    finally { setBusy(false); }
  }, [id]);

  useEffect(() => { load(); const t = setInterval(load, 15_000); return () => clearInterval(t); }, [load]);

  const stats = campaign?.stats || {};
  const pct = (n, of) => of > 0 ? Math.round(100 * (n || 0) / of) : 0;
  const bar = (label, n, of, color) => (
    <div style={{ marginBottom: 6, fontSize: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>{label}</span><span>{n || 0} / {of || 0} ({pct(n, of)}%)</span></div>
      <div style={{ height: 8, background: '#f1f1f1', borderRadius: 4 }}>
        <div style={{ height: 8, width: `${pct(n, of)}%`, background: color, borderRadius: 4, transition: 'width .3s' }} />
      </div>
    </div>
  );

  return (
    <SidePanel title={campaign?.name || 'Loading…'} onClose={onClose}>
      {busy && !campaign && <div className="em-empty">Loading…</div>}
      {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
      {campaign && (
        <>
          <div style={{ fontSize: 12, color: '#525252', marginBottom: 12 }}>
            <span style={{ background: '#eef2ff', padding: '2px 8px', borderRadius: 10 }}>{campaign.status}</span>
            {campaign.scheduledAt && <span style={{ marginLeft: 8 }}>scheduled: {fmtCell(campaign.scheduledAt)}</span>}
            {campaign.startedAt && <span style={{ marginLeft: 8 }}>started: {fmtCell(campaign.startedAt)}</span>}
            {campaign.completedAt && <span style={{ marginLeft: 8 }}>completed: {fmtCell(campaign.completedAt)}</span>}
          </div>
          <div style={{ marginBottom: 16 }}>
            {bar('Queued',       stats.queued,       stats.audienceSize, '#0F62FE')}
            {bar('Sent',         stats.sent,         stats.audienceSize, '#42BE65')}
            {bar('Opened',       stats.opened,       stats.sent,         '#F1C21B')}
            {bar('Clicked',      stats.clicked,      stats.sent,         '#FF832B')}
            {bar('Bounced',      stats.bounced,      stats.audienceSize, '#a71d2a')}
            {bar('Unsubscribed', stats.unsubscribed, stats.sent,         '#888')}
          </div>
          <h6 className="mb-2">Recent recipients ({msgs.length})</h6>
          {msgs.length === 0 && <div className="em-empty">No sends yet.</div>}
          {msgs.map((m) => (
            <div key={m._id}
                 onClick={() => { onPickMessage && onPickMessage(m._id); onClose(); }}
                 style={{ display: 'flex', gap: 8, padding: '6px 8px', borderBottom: '1px solid #f4f4f4', fontSize: 12, cursor: 'pointer' }}>
              <span style={{ width: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.to?.[0]?.address}>{m.to?.[0]?.address || '?'}</span>
              <span style={{ width: 70, color: m.status === 'sent' ? '#1e6b3e' : m.status === 'bounced' || m.status === 'failed' ? '#a71d2a' : '#525252' }}>{m.status}</span>
              {m.openedAt && <span style={{ color: '#856404', fontSize: 11 }}>opened</span>}
              {m.clickedAt && <span style={{ color: '#a35200', fontSize: 11 }}>clicked</span>}
              <span style={{ marginLeft: 'auto', color: '#888', fontSize: 11 }}>{fmtCell(m.sentAt || m.createdAt)}</span>
            </div>
          ))}
        </>
      )}
    </SidePanel>
  );
}

// MessageDetailPanel — inbox row click opens this. Shows the inbound
// message + the LLM classification + the drafted reply with editable
// body and Approve / Discard buttons. Approve flips the draft to
// 'queued' and the worker's email-send queue mails it.
function MessageDetailPanel({ id, canSend, onClose, onAction }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editedBody, setEditedBody] = useState('');
  const [editedSubject, setEditedSubject] = useState('');
  const [err, setErr] = useState(null);
  const [thread, setThread] = useState(null);   // null = collapsed; [] or rows = expanded
  const [threadBusy, setThreadBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      const r = await get(`/api/email/messages/${id}`);
      const d = (r && r.data) || null;
      setData(d);
      setEditedBody(d?._draft?.bodyText || '');
      setEditedSubject(d?._draft?.subject || '');
    } catch (e) { setErr((e && e.message) || 'Failed to load'); }
    finally { setBusy(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    if (!data?._draft) return;
    setBusy(true); setErr(null);
    try {
      await post(`/api/email/messages/${data._draft._id}/approve`, { bodyText: editedBody, subject: editedSubject }, false);
      onAction && onAction();
      onClose && onClose();
    } catch (e) { setErr((e && e.message) || 'Approve failed'); }
    finally { setBusy(false); }
  };
  const discard = async () => {
    if (!data?._draft) return;
    if (!window.confirm('Discard this draft reply?')) return;
    setBusy(true); setErr(null);
    try {
      await post(`/api/email/messages/${data._draft._id}/discard`, {}, false);
      onAction && onAction();
      onClose && onClose();
    } catch (e) { setErr((e && e.message) || 'Discard failed'); }
    finally { setBusy(false); }
  };

  const m = data;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'stretch', justifyContent: 'flex-end' }}
         onClick={onClose}>
      <div style={{ background: '#fff', width: 720, maxWidth: '92vw', maxHeight: '100vh', overflowY: 'auto', padding: 20 }}
           onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-baseline justify-content-between mb-2">
          <h5 className="mb-0">{m?.subject || 'Loading…'}</h5>
          <button className="acc-btn" onClick={onClose}>Close</button>
        </div>
        {busy && !m && <div className="em-empty">Loading…</div>}
        {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
        {m && (
          <>
            <div style={{ fontSize: 12, color: '#525252', marginBottom: 12 }}>
              <div><strong>From:</strong> {m.from?.name || ''} &lt;{m.from?.address || ''}&gt;</div>
              <div><strong>Received:</strong> {fmtCell(m.receivedAt)}</div>
              {m.intentLabel && (
                <div style={{ marginTop: 6 }}>
                  <span style={{ background: '#eef2ff', padding: '2px 8px', borderRadius: 10 }}>{m.intentLabel}</span>
                  {typeof m.intentConfidence === 'number' && <span style={{ marginLeft: 6, color: '#888' }}>confidence: {(m.intentConfidence * 100).toFixed(0)}%</span>}
                </div>
              )}
              {m.intentReasoning && <div style={{ fontStyle: 'italic', color: '#666', marginTop: 4 }}>{m.intentReasoning}</div>}
            </div>
            <div style={{ background: '#fafbfc', border: '1px solid #eee', borderRadius: 4, padding: 10, marginBottom: 12, fontSize: 13, whiteSpace: 'pre-wrap' }}>
              {m.bodyText || '(no plain-text body)'}
            </div>

            {(m.attachments || []).filter((a) => a && a.storageKey).length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <h6 className="mb-1" style={{ fontSize: 13 }}>Attachments ({(m.attachments || []).filter((a) => a.storageKey).length})</h6>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {(m.attachments || []).map((a, i) => a.storageKey && (
                    <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '4px 8px', background: '#fafbfc', border: '1px solid #eee', borderRadius: 4, marginBottom: 4 }}>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.filename}>{a.filename}</span>
                      <span style={{ color: '#888', fontSize: 11 }}>{a.contentType}</span>
                      <span style={{ color: '#888', fontSize: 11 }}>{((a.sizeBytes || 0) / 1024).toFixed(1)} KB</span>
                      <button
                        className="acc-btn"
                        type="button"
                        onClick={async () => {
                          try {
                            const r = await get(`/api/email/messages/${m._id}/attachment/${i}`);
                            const url = r && r.data && r.data.downloadUrl;
                            if (url) window.open(url, '_blank');
                          } catch (_) { /* ignored — backend logs the failure */ }
                        }}
                      >
                        Download
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {m.threadKey && thread === null && (
              <button className="acc-btn" disabled={threadBusy}
                onClick={async () => {
                  setThreadBusy(true);
                  try {
                    const r = await get(`/api/email/messages/thread/${encodeURIComponent(m.threadKey)}`);
                    setThread((r && r.data) || []);
                  } catch (_) { setThread([]); }
                  finally { setThreadBusy(false); }
                }}>
                {threadBusy ? 'Loading thread…' : 'View full thread'}
              </button>
            )}
            {thread && thread.length > 0 && (
              <div style={{ borderTop: '1px solid #eee', margin: '12px 0', paddingTop: 12 }}>
                <h6 className="mb-2">Conversation ({thread.length} message{thread.length === 1 ? '' : 's'})</h6>
                {thread.map((t) => (
                  <div key={t._id} style={{
                    borderLeft: t.direction === 'inbound' ? '3px solid #0F62FE' : '3px solid #1e6b3e',
                    padding: '6px 10px', marginBottom: 8, background: t._id === m._id ? '#fff8d6' : '#fafbfc',
                    fontSize: 12,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#525252' }}>
                      <span>{t.direction === 'inbound' ? '←' : '→'} {t.from?.address || t.to?.[0]?.address || '?'}</span>
                      <span style={{ color: '#888' }}>{fmtCell(t.receivedAt || t.sentAt)}</span>
                    </div>
                    <div style={{ fontWeight: 500, marginTop: 2 }}>{t.subject || '(no subject)'}</div>
                    <div style={{ marginTop: 4, whiteSpace: 'pre-wrap', maxHeight: 120, overflowY: 'auto', color: '#15172b' }}>
                      {(t.bodyText || t.snippet || '').slice(0, 1500)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {m._draft && (
              <div style={{ borderTop: '1px solid #eee', paddingTop: 12 }}>
                <h6 className="mb-2">Drafted reply <span style={{ fontSize: 11, color: '#888', fontWeight: 400 }}>(status: {m._draft.status})</span></h6>
                <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, color: '#525252', gap: 2, marginBottom: 8 }}>
                  <span>Subject</span>
                  <input value={editedSubject} onChange={(e) => setEditedSubject(e.target.value)}
                         style={{ fontSize: 12, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4 }} />
                </label>
                <textarea value={editedBody} onChange={(e) => setEditedBody(e.target.value)}
                          rows={10}
                          style={{ width: '100%', fontSize: 13, padding: 8, border: '1px solid #ddd', borderRadius: 4, fontFamily: 'inherit' }} />
                {m._draft.status === 'draft' && canSend && (
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
                    <button className="acc-btn" disabled={busy} onClick={discard}>Discard</button>
                    <button className="acc-btn acc-btn-primary" disabled={busy} onClick={approve}>Approve & send</button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// GenerateSequenceModal — kicks off AI generation of a drip sequence for
// a brand. Mirrors the "Generate" affordance of the Ideas section: pick a
// brand (+ optional goal), the worker drafts the steps, and the new
// sequence shows up in the list (draft, or auto-approved per brand
// settings) on the next refresh.
function GenerateSequenceModal({ onClose, onQueued }) {
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState('');
  const [goal, setGoal] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => { (async () => {
    try { const r = await get('/brands'); setBrands((r && r.data) || []); }
    catch (e) { setErr((e && e.message) || 'Failed to load brands'); }
  })(); }, []);

  const submit = async () => {
    if (!brandId) return setErr('Pick a brand');
    setBusy(true); setErr(null);
    try {
      await post('/api/email/sequences/generate', { brand_id: brandId, goal }, false);
      onQueued && onQueued();
    } catch (e) { setErr((e && e.message) || 'Generate failed'); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', width: 560, maxWidth: '92vw', borderRadius: 8, padding: 20 }} onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-baseline justify-content-between mb-3">
          <h5 className="mb-0">Generate email sequence</h5>
          <button className="acc-btn" onClick={onClose}>Close</button>
        </div>
        <p className="em-stub mb-3">The AI drafts a brand-voiced drip sequence (seed email + timed follow-ups). You review it here; if the brand requires approval it lands as a draft, otherwise it's auto-approved and ready to enroll a list.</p>
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, color: '#525252', gap: 2, marginBottom: 10 }}>
          <span>Brand *</span>
          <select value={brandId} onChange={(e) => setBrandId(e.target.value)} style={{ fontSize: 12, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4 }}>
            <option value="">— pick brand —</option>
            {brands.map((b) => (<option key={b._id} value={b._id}>{b.name}</option>))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, color: '#525252', gap: 2, marginBottom: 10 }}>
          <span>Goal (optional)</span>
          <textarea value={goal} onChange={(e) => setGoal(e.target.value)} rows={3} placeholder="e.g. Warm up cold prospects from the trade-show list and book demos" style={{ fontSize: 12, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4 }} />
        </label>
        {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="acc-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="acc-btn acc-btn-primary" onClick={submit} disabled={busy}>{busy ? 'Queuing…' : 'Generate'}</button>
        </div>
      </div>
    </div>
  );
}

// Render a body/template with sample contact values so the operator sees
// what a recipient gets. Unknown {{tokens}} render as [token].
function renderPreview(text) {
  return String(text || '')
    .replace(/\{\{\s*firstName\s*\}\}/gi, 'Jane')
    .replace(/\{\{\s*lastName\s*\}\}/gi, 'Doe')
    .replace(/\{\{\s*company_name\s*\}\}/gi, 'Acme Co')
    .replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, k) => `[${k}]`);
}

// SequenceDetailPanel — sequence row click. Reviews the generated steps,
// allows editing while in 'draft', Approve (draft→approved), and once
// approved, Enroll a list (+ pick a mailbox) to start the per-contact
// drip, or Stop all active enrollments.
// RichHtmlEditor — dependency-free rich-text editor over contentEditable.
// Toolbar: bold/italic/underline, text color, highlight, heading, lists,
// link, clear formatting. Emits HTML via onChange. Used for sequence step
// bodies so operators edit formatted email copy without touching raw HTML.
function RichHtmlEditor({ html, onChange, minHeight = 160 }) {
  const ref = React.useRef(null);
  // undefined ≠ any string → guarantees the FIRST effect run paints the canvas
  // (initializing to `html` made mount a no-op: the editable area showed empty).
  const lastEmitted = React.useRef(undefined);
  useEffect(() => {
    if (ref.current && (html || '') !== lastEmitted.current) {
      ref.current.innerHTML = html || '';
      lastEmitted.current = html || '';
    }
  }, [html]);
  const emit = () => {
    const v = ref.current ? ref.current.innerHTML : '';
    lastEmitted.current = v;
    onChange(v);
  };
  const cmd = (c, val) => { document.execCommand(c, false, val || undefined); emit(); ref.current && ref.current.focus(); };
  const link = () => { const u = window.prompt('Link URL (https://…)'); if (u) cmd('createLink', u); };
  const btn = { fontSize: 12, padding: '3px 8px', border: '1px solid #ddd', background: '#fff', borderRadius: 4, cursor: 'pointer' };
  return (
    <div style={{ border: '1px solid #ddd', borderRadius: 6, overflow: 'hidden' }}>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', padding: 6, background: '#f9fafb', borderBottom: '1px solid #eee', alignItems: 'center' }}>
        <button type="button" style={{ ...btn, fontWeight: 700 }} onMouseDown={(e) => { e.preventDefault(); cmd('bold'); }}>B</button>
        <button type="button" style={{ ...btn, fontStyle: 'italic' }} onMouseDown={(e) => { e.preventDefault(); cmd('italic'); }}>I</button>
        <button type="button" style={{ ...btn, textDecoration: 'underline' }} onMouseDown={(e) => { e.preventDefault(); cmd('underline'); }}>U</button>
        <label style={{ ...btn, display: 'inline-flex', alignItems: 'center', gap: 4 }} onMouseDown={(e) => e.stopPropagation()}>A
          <input type="color" defaultValue="#0b3d91" style={{ width: 18, height: 18, border: 'none', padding: 0, background: 'transparent' }}
            onChange={(e) => cmd('foreColor', e.target.value)} title="Text color" /></label>
        <label style={{ ...btn, display: 'inline-flex', alignItems: 'center', gap: 4 }} onMouseDown={(e) => e.stopPropagation()}>🖍
          <input type="color" defaultValue="#fff3bf" style={{ width: 18, height: 18, border: 'none', padding: 0, background: 'transparent' }}
            onChange={(e) => cmd('hiliteColor', e.target.value)} title="Highlight" /></label>
        <button type="button" style={btn} onMouseDown={(e) => { e.preventDefault(); cmd('formatBlock', '<h2>'); }}>H2</button>
        <button type="button" style={btn} onMouseDown={(e) => { e.preventDefault(); cmd('formatBlock', '<p>'); }}>¶</button>
        <button type="button" style={btn} onMouseDown={(e) => { e.preventDefault(); cmd('insertUnorderedList'); }}>• List</button>
        <button type="button" style={btn} onMouseDown={(e) => { e.preventDefault(); cmd('insertOrderedList'); }}>1. List</button>
        <button type="button" style={btn} onMouseDown={(e) => { e.preventDefault(); link(); }}>🔗 Link</button>
        <button type="button" style={btn} onMouseDown={(e) => { e.preventDefault(); cmd('removeFormat'); }}>Clear</button>
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning onInput={emit}
        style={{ minHeight, padding: 12, fontSize: 14, lineHeight: 1.6, color: '#15172b', outline: 'none', background: '#fff' }} />
    </div>
  );
}

// EmailStepModal — full-screen editor/preview for ONE sequence email.
// Two modes: 'edit' (rich formatting canvas + Render preview) and 'preview'
// ("as the customer sees it" — rendered through the brand envelope — with an
// instant ✏️ Edit switch). Save persists immediately; closing with unsaved
// changes always asks Save / Discard / Keep editing.
function EmailStepModal({ brandId, subject, initialHtml, initialMode, onPersist, onClose }) {
  const [mode, setMode] = useState(initialMode === 'edit' ? 'edit' : 'preview');
  const [html, setHtml] = useState(initialHtml || '');
  const [savedHtml, setSavedHtml] = useState(initialHtml || '');
  const [renderHtml, setRenderHtml] = useState('');
  const [confirmClose, setConfirmClose] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const dirty = html !== savedHtml;

  // Debounced envelope render — used by preview mode AND the edit-mode
  // "Render preview" pane; always reflects the CURRENT (unsaved) html.
  useEffect(() => {
    let dead = false;
    const t = setTimeout(async () => {
      try {
        const r = await post('/api/email/templates/preview', { brand_id: brandId, subject, bodyHtml: html }, false);
        if (!dead) setRenderHtml((r && r.data && r.data.html) || '');
      } catch (_) { /* keep last render */ }
    }, 350);
    return () => { dead = true; clearTimeout(t); };
  }, [brandId, subject, html]);

  const save = async (closeAfter) => {
    setBusy(true); setErr(null);
    try {
      await onPersist(html);
      setSavedHtml(html);
      setConfirmClose(false);
      if (closeAfter) onClose();
    } catch (e) { setErr((e && e.message) || 'Save failed'); }
    finally { setBusy(false); }
  };
  const requestClose = () => { if (dirty) setConfirmClose(true); else onClose(); };

  const modeBtn = (m, label) => (
    <button className={`btn btn-sm m-0 ${mode === m ? 'btn-primary' : 'btn-outline-secondary'}`}
      style={{ fontSize: 11, padding: '3px 10px' }} onClick={() => setMode(m)}>{label}</button>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={requestClose}>
      <div style={{ background: '#fff', width: 860, maxWidth: '96vw', maxHeight: '94vh', borderRadius: 10, overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ borderBottom: '1px solid #e5e7eb', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{subject || '(no subject)'}</div>
            <div style={{ fontSize: 11, color: '#9ca3af' }}>{mode === 'preview' ? "Exactly as the recipient's inbox renders it" : 'Edit the email body — formatting is preserved in the send'}</div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {modeBtn('edit', '✏️ Edit')}
            {modeBtn('preview', '👁 Customer view')}
            {dirty && <span style={{ fontSize: 11, color: '#b45309', fontWeight: 600 }}>● unsaved</span>}
            <button className="acc-btn" onClick={requestClose}>Close</button>
          </div>
        </div>
        {err && <div className="alert alert-warning m-2" style={{ fontSize: 12 }}>{err}</div>}
        <div style={{ flex: 1, overflowY: 'auto', background: mode === 'preview' ? '#f4f5f7' : '#fff', padding: mode === 'edit' ? 12 : 0 }}>
          {mode === 'edit' ? (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 380px', minWidth: 320 }}>
                <RichHtmlEditor html={html} onChange={setHtml} minHeight={380} />
              </div>
              <div style={{ flex: '1 1 320px', minWidth: 300, border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden', alignSelf: 'stretch' }}>
                <div style={{ fontSize: 10, color: '#9ca3af', padding: '4px 8px', background: '#f9fafb', borderBottom: '1px solid #eee', textTransform: 'uppercase', letterSpacing: 0.5 }}>Rendered preview (live)</div>
                <iframe sandbox="allow-same-origin" srcDoc={renderHtml || '<div style="padding:24px;font-family:sans-serif;color:#9ca3af">Rendering…</div>'} style={{ width: '100%', height: 400, border: 'none' }} title="Live render" />
              </div>
            </div>
          ) : (
            <iframe sandbox="allow-same-origin" srcDoc={renderHtml || '<div style="padding:32px;font-family:sans-serif;color:#9ca3af">Rendering…</div>'} style={{ width: '100%', height: '68vh', border: 'none', display: 'block' }} title="Inbox preview" />
          )}
        </div>
        <div style={{ borderTop: '1px solid #e5e7eb', padding: '10px 16px', background: '#fff' }}>
          {confirmClose ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: '#b45309', fontWeight: 600, marginRight: 'auto' }}>You have unsaved changes.</span>
              <button className="acc-btn acc-btn-primary" disabled={busy} onClick={() => save(true)}>{busy ? 'Saving…' : 'Save & close'}</button>
              <button className="acc-btn" disabled={busy} onClick={onClose}>Discard changes</button>
              <button className="acc-btn" disabled={busy} onClick={() => setConfirmClose(false)}>Keep editing</button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="acc-btn acc-btn-primary" disabled={busy || !dirty} onClick={() => save(false)}>{busy ? 'Saving…' : dirty ? 'Save' : 'Saved'}</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SequenceDetailPanel({ id, perms, onClose, onChanged }) {
  const [seq, setSeq] = useState(null);
  const [steps, setSteps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [lists, setLists] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [enrollList, setEnrollList] = useState('');
  const [enrollAccount, setEnrollAccount] = useState('');
  const [enrollPeriod, setEnrollPeriod] = useState('month'); // '' = all at once; else spread enrollment over day/week/month
  const [resetWarmup, setResetWarmup] = useState(true); // restart the mailbox warm-up ramp on launch
  const [previewOn, setPreviewOn] = useState(false);
  const [stepModal, setStepModal] = useState(null); // {index, mode:'edit'|'preview'}
  const [poolSel, setPoolSel] = useState(null);     // Set of account ids in the sending pool
  const [distBusy, setDistBusy] = useState(false);
  const [distResult, setDistResult] = useState(null);
  const [showAddMailbox, setShowAddMailbox] = useState(false);

  const loadAccounts = useCallback(async () => {
    try { const a = await get('/api/email/accounts'); setAccounts((a && a.data) || []); } catch (_) {}
  }, []);

  const load = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      const r = await get(`/api/email/sequences/${id}`);
      const d = (r && r.data) || null;
      setSeq(d);
      setSteps((d && d.steps) || []);
      setEnrollAccount(d && d.account_id ? String(d.account_id) : '');
      const pool = (d && Array.isArray(d.account_ids) && d.account_ids.length)
        ? d.account_ids.map(String)
        : (d && d.account_id ? [String(d.account_id)] : []);
      setPoolSel(new Set(pool));
    } catch (e) { setErr((e && e.message) || 'Failed to load'); }
    finally { setBusy(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { (async () => {
    try { const l = await get('/api/email/lists'); setLists((l && l.data) || []); } catch (_) {}
    loadAccounts();
  })(); }, [loadAccounts]);

  const isDraft = seq && seq.status === 'draft';
  const isApproved = seq && seq.status === 'approved';

  const setStep = (i, k, v) => setSteps((arr) => arr.map((s, idx) => idx === i ? { ...s, [k]: v } : s));
  // A step's editable HTML: stored bodyHtml, else paragraphs built from bodyText.
  // Persist ONE step's edited html immediately (modal Save): updates local
  // state and PUTs the full steps array so the server copy matches.
  const persistStepHtml = async (i, v) => {
    const tmp = document.createElement('div'); tmp.innerHTML = v;
    const next = steps.map((x, idx) => idx === i ? { ...x, bodyHtml: v, bodyText: (tmp.innerText || '').trim() } : x);
    setSteps(next);
    const { putGeneral } = await import('../../api');
    await putGeneral(`/api/email/sequences/${id}`, { name: seq.name, goal: seq.goal, steps: next });
    onChanged && onChanged();
  };
  const stepHtml = (s) => (s.bodyHtml && s.bodyHtml.trim())
    ? s.bodyHtml
    : (s.bodyText || '').split(/\n{2,}/).filter(Boolean).map((p) => `<p>${p.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>')}</p>`).join('');
  const addStep = () => setSteps((arr) => [...arr, { subject: '', bodyText: '', delayValue: 2, delayUnit: 'days' }]);
  const removeStep = (i) => setSteps((arr) => arr.filter((_, idx) => idx !== i));

  const saveDraft = async () => {
    setBusy(true); setErr(null);
    try {
      const { putGeneral } = await import('../../api');
      await putGeneral(`/api/email/sequences/${id}`, { name: seq.name, goal: seq.goal, steps });
      await load(); onChanged && onChanged();
    } catch (e) { setErr((e && e.message) || 'Save failed'); }
    finally { setBusy(false); }
  };
  const approve = async () => {
    setBusy(true); setErr(null);
    try { await post(`/api/email/sequences/${id}/approve`, {}, false); await load(); onChanged && onChanged(); }
    catch (e) { setErr((e && e.message) || 'Approve failed'); }
    finally { setBusy(false); }
  };
  const enroll = async () => {
    if (!enrollList) return setErr('Pick a list to enroll');
    if (!enrollAccount) return setErr('Pick a mailbox to send from');
    setBusy(true); setErr(null);
    try {
      const r = await post(`/api/email/sequences/${id}/enroll`, { list_id: enrollList, account_id: enrollAccount, period: enrollPeriod || undefined, resetWarmup }, false);
      const d = (r && r.data) || {};
      const spread = d.spreadDays > 0 ? ` Start times spread over ${d.spreadDays >= 1 ? d.spreadDays + ' day(s)' : 'the period'}.` : '';
      const warm = d.warmupReset ? ' Mailbox warm-up restarted at day 1 (10/day, ramping up).' : (d.warmupResetSkipped ? ` Warm-up not reset (${d.warmupResetSkipped}).` : '');
      window.alert(`Enrolled ${d.enrolled || 0} of ${d.audience || 0} contacts. The drip starts at each step's delay.${spread}${warm}`);
      await load(); onChanged && onChanged();
    } catch (e) { setErr((e && e.message) || 'Enroll failed'); }
    finally { setBusy(false); }
  };
  const stop = async () => {
    if (!window.confirm(
      'Stop all active enrollments for this sequence?\n\n'
      + 'This is ONE-WAY: every enrollment is marked stopped and its place in the drip is '
      + 'discarded, so it cannot be resumed. Use Pause if you only want it to hold.'
    )) return;
    setBusy(true); setErr(null);
    try { const r = await post(`/api/email/sequences/${id}/stop`, {}, false); window.alert(`Stopped ${(r && r.data && r.data.stopped) || 0} enrollment(s).`); await load(); onChanged && onChanged(); }
    catch (e) { setErr((e && e.message) || 'Stop failed'); }
  };

  // Pause holds the sequence where it is: the drip scheduler skips it, every
  // enrollment keeps its nextStepAt, and resuming continues from exactly there.
  const togglePause = async () => {
    const next = !seq.paused;
    setBusy(true); setErr('');
    try {
      const r = await post(`/api/email/sequences/${id}/pause`, { paused: next }, false);
      const d = (r && r.data) || {};
      window.alert(next
        ? `Paused. ${d.held || 0} enrollment(s) are held in place — nothing will send until you resume.`
        : 'Resumed. It continues from exactly where it left off.');
      await load(); onChanged && onChanged();
    } catch (e) { setErr((e && e.message) || 'Could not change it'); }
    finally { setBusy(false); }
  };
  const clone = async () => {
    setBusy(true); setErr(null);
    try { const r = await post(`/api/email/sequences/${id}/clone`, {}, false); window.alert('Cloned as a new draft.'); onChanged && onChanged(); onClose && onClose(); return r; }
    catch (e) { setErr((e && e.message) || 'Clone failed'); }
    finally { setBusy(false); }
  };
  const togglePool = (aid) => setPoolSel((prev) => {
    const n = new Set(prev || []);
    if (n.has(aid)) n.delete(aid); else n.add(aid);
    return n;
  });
  // Add extra mailboxes to a LIVE sequence: re-pin every active contact across
  // the selected pool by stable hash (a contact sent from A is never sent from
  // B) so throughput = N × the per-mailbox daily cap; fresh boxes start warming.
  const distribute = async () => {
    const ids = Array.from(poolSel || []);
    if (!ids.length) return setErr('Pick at least one mailbox for the sending pool');
    setDistBusy(true); setErr(null); setDistResult(null);
    try {
      const r = await post(`/api/email/sequences/${id}/distribute`, { account_ids: ids }, false);
      const d = (r && r.data) || {};
      setDistResult(d);
      window.alert(`Re-pinned ${(d.moved || 0).toLocaleString()} of ${(d.scanned || 0).toLocaleString()} active contacts across ${d.mailboxes || ids.length} mailbox(es). Any newly-added mailbox starts warming up now (10/day, ramping).`);
      await load(); onChanged && onChanged();
    } catch (e) { setErr((e && (e.response?.data?.error || e.message)) || 'Distribute failed'); }
    finally { setDistBusy(false); }
  };

  // Send EVERY step instantly to one inbox, no warm-up — a true inbox preview.
  const sendTest = async () => {
    const to = window.prompt('Send a test of ALL emails in this sequence, instantly (no warm-up), to:', '');
    if (!to) return;
    setBusy(true); setErr(null);
    try {
      const acct = enrollAccount || (seq && seq.account_id) || '';
      const r = await post(`/api/email/sequences/${id}/test`, { email: to.trim(), account_id: acct || undefined }, false);
      const d = (r && r.data) || {};
      window.alert(`Test sent: ${d.sent || 0} email(s) to ${d.to || to}${d.mailbox ? ` from ${d.mailbox}` : ''}. Check the inbox — they land immediately.`);
    } catch (e) { setErr((e && (e.response?.data?.error || e.message)) || 'Test send failed'); }
    finally { setBusy(false); }
  };

  return (
    <SidePanel title={seq ? seq.name : 'Loading…'} onClose={onClose}>
      {busy && !seq && <div className="em-empty">Loading…</div>}
      {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
      {seq && (
        <>
          <div style={{ fontSize: 12, color: '#525252', marginBottom: 12 }}>
            <span style={{ background: seq.status === 'approved' ? '#e7f5ec' : '#eef2ff', color: seq.status === 'approved' ? '#1e6b3e' : '#15172b', padding: '2px 8px', borderRadius: 10 }}>{seq.status}</span>
            {seq.enrollments && <span style={{ marginLeft: 8 }}>{seq.enrollments.active} active · {seq.enrollments.completed} completed · {seq.enrollments.total} total</span>}
            {seq.goal && <div style={{ marginTop: 6, fontStyle: 'italic', color: '#666' }}>{seq.goal}</div>}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <h6 className="mb-0">Steps ({steps.length})</h6>
            <label style={{ fontSize: 11, color: '#525252', display: 'flex', alignItems: 'center', gap: 4 }}>
              <input type="checkbox" checked={previewOn} onChange={(e) => setPreviewOn(e.target.checked)} /> Preview
            </label>
          </div>
          {steps.map((s, i) => (
            <div key={i} style={{ border: '1px solid #eee', borderRadius: 6, padding: 10, marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontWeight: 600, fontSize: 12 }}>Step {i + 1}</span>
                <span style={{ fontSize: 11, color: '#888' }}>{i === 0 ? 'after enrollment:' : 'after previous email:'}</span>
                <input type="number" min="0" value={s.delayValue ?? 0} onChange={(e) => setStep(i, 'delayValue', Number(e.target.value))} style={{ width: 64, fontSize: 12, padding: '3px 6px', border: '1px solid #ddd', borderRadius: 4 }} />
                <select value={s.delayUnit || 'hours'} onChange={(e) => setStep(i, 'delayUnit', e.target.value)} style={{ fontSize: 12, padding: '3px 6px', border: '1px solid #ddd', borderRadius: 4 }}>
                  <option value="hours">hours</option>
                  <option value="days">days</option>
                </select>
                <button className="acc-btn" style={{ marginLeft: 'auto' }} onClick={() => removeStep(i)}>×</button>
              </div>
              {previewOn ? (
                <div style={{ background: '#fafbfc', border: '1px solid #eee', borderRadius: 4, padding: 10 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{renderPreview(s.subject) || '(no subject)'}</div>
                  <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', color: '#15172b' }}>{renderPreview(s.bodyText) || '(empty)'}</div>
                </div>
              ) : (
                <>
                  <input value={s.subject || ''} onChange={(e) => setStep(i, 'subject', e.target.value)} placeholder="Subject" style={{ width: '100%', fontSize: 12, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4, marginBottom: 6 }} />
                  <RichHtmlEditor
                    html={stepHtml(s)}
                    onChange={(v) => {
                      // Keep the plain-text twin in sync so tracking footers,
                      // spam-text parts, and older flows all stay correct.
                      const tmp = document.createElement('div'); tmp.innerHTML = v;
                      setSteps((arr) => arr.map((x, idx) => idx === i ? { ...x, bodyHtml: v, bodyText: (tmp.innerText || '').trim() } : x));
                    }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 6 }}>
                    <button className="acc-btn" style={{ fontSize: 11 }} onClick={() => setStepModal({ index: i, mode: 'edit' })}>
                      ✏️ Edit email
                    </button>
                    <button className="acc-btn" style={{ fontSize: 11 }} onClick={() => setStepModal({ index: i, mode: 'preview' })}>
                      👁 Preview as the customer sees it
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginBottom: 16, flexWrap: 'wrap' }}>
            <button className="acc-btn" onClick={addStep}>+ Add step</button>
            <button className="acc-btn" disabled={busy || !steps.length} onClick={sendTest} title="Send every email in this sequence instantly to one inbox — no warm-up, a real inbox preview">✉️ Send test</button>
            {perms.canAdd && <button className="acc-btn" disabled={busy} onClick={clone}>Clone</button>}
            {perms.canEdit && <button className="acc-btn" disabled={busy} onClick={saveDraft}>Save</button>}
            {isDraft && perms.canLaunch && <button className="acc-btn acc-btn-primary" disabled={busy} onClick={approve}>Approve</button>}
          </div>

          {isApproved && perms.canLaunch && (
            <div style={{ borderTop: '1px solid #eee', paddingTop: 12, marginTop: 8 }}>
              <h6 className="mb-2">Enroll a list</h6>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <label style={{ fontSize: 11, color: '#525252' }}>Audience list
                  <select value={enrollList} onChange={(e) => setEnrollList(e.target.value)} style={{ width: '100%', fontSize: 12, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4 }}>
                    <option value="">— pick list —</option>
                    {lists.map((l) => (<option key={l._id} value={l._id}>{l.name} ({l.memberCount || 0})</option>))}
                  </select>
                </label>
                <label style={{ fontSize: 11, color: '#525252' }}>Send from mailbox
                  <select value={enrollAccount} onChange={(e) => setEnrollAccount(e.target.value)} style={{ width: '100%', fontSize: 12, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4 }}>
                    <option value="">— pick mailbox —</option>
                    {accounts.map((a) => (<option key={a._id} value={a._id} disabled={a.marketingSenderDisabled}>{a.name} &lt;{a.fromAddress}&gt;{a.marketingSenderDisabled ? ' — marketing off' : ''}</option>))}
                  </select>
                </label>
                <label style={{ fontSize: 11, color: '#525252' }}>Spread enrollment over
                  <select value={enrollPeriod} onChange={(e) => setEnrollPeriod(e.target.value)} title="Stagger contacts' start times evenly across this span instead of starting everyone at once" style={{ width: '100%', fontSize: 12, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4 }}>
                    <option value="">all at once</option>
                    <option value="day">over a day</option>
                    <option value="week">over a week</option>
                    <option value="month">over a month (recommended)</option>
                  </select>
                </label>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                {perms.canEdit && (
                  <button className="acc-btn" disabled={busy} onClick={togglePause}
                    title={seq.paused
                      ? 'Let it run again from exactly where it left off'
                      : 'Hold every enrollment in place — nothing sends, nothing is lost, resume any time'}>
                    {seq.paused ? '▶ Resume' : '⏸ Pause'}
                  </button>
                )}
                {perms.canEdit && <button className="acc-btn" disabled={busy} onClick={stop}>Stop all</button>}
                <label style={{ fontSize: 11, color: '#525252', display: 'flex', alignItems: 'center', gap: 6, marginRight: 'auto' }} title="Restart this mailbox's warm-up ramp so a fresh push starts at 10/day and ramps up — protects deliverability on large/cold lists.">
                  <input type="checkbox" checked={resetWarmup} onChange={(e) => setResetWarmup(e.target.checked)} />
                  Reset mailbox warm-up on launch (recommended for cold lists)
                </label>
                <button className="acc-btn acc-btn-primary" disabled={busy} onClick={enroll}>Enroll list</button>
              </div>

              <div style={{ borderTop: '1px dashed #e5e5e5', paddingTop: 12, marginTop: 14 }}>
                <h6 className="mb-1">Sending mailboxes (pool)</h6>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>
                  Add more mailboxes to spread this live sequence's load. Each contact is pinned to exactly one box, so no one is emailed twice. Newly-added mailboxes begin warming up automatically.
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 160, overflowY: 'auto', border: '1px solid #eee', borderRadius: 4, padding: 8, marginBottom: 8 }}>
                  {accounts
                    .filter((a) => !seq.brand_id || !a.brand_id || String(a.brand_id) === String(seq.brand_id))
                    .map((a) => {
                      const aid = String(a._id);
                      const dist = distResult && Array.isArray(distResult.distribution)
                        ? distResult.distribution.find((x) => String(x.account_id) === aid) : null;
                      const mktOff = !!a.marketingSenderDisabled;
                      return (
                        <label key={aid} title={mktOff ? (a.marketingSenderDisabledReason || 'Disabled as a marketing sender') : undefined} style={{ fontSize: 12, color: mktOff ? '#999' : '#333', display: 'flex', alignItems: 'center', gap: 8, cursor: mktOff ? 'not-allowed' : 'pointer' }}>
                          <input type="checkbox" disabled={mktOff} checked={!mktOff && !!(poolSel && poolSel.has(aid))} onChange={() => togglePool(aid)} />
                          <span style={{ flex: 1, textDecoration: mktOff ? 'line-through' : 'none' }}>{a.name} <span style={{ color: '#999' }}>&lt;{a.fromAddress}&gt;</span></span>
                          {mktOff && <span style={{ color: '#a71d2a', fontSize: 10, fontWeight: 600 }}>marketing off · not usable</span>}
                          {a.sendingPaused && <span style={{ color: '#a71d2a', fontSize: 10 }}>paused</span>}
                          {dist && <span style={{ color: '#1e6b3e', fontSize: 11 }}>{(dist.enrollments || 0).toLocaleString()} contacts</span>}
                        </label>
                      );
                    })}
                  {accounts.filter((a) => !seq.brand_id || !a.brand_id || String(a.brand_id) === String(seq.brand_id)).length === 0 && (
                    <span style={{ fontSize: 12, color: '#999' }}>No mailboxes connected for this brand. Connect one in the Mailboxes tab first.</span>
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, color: '#888', marginRight: 'auto' }}>{poolSel ? poolSel.size : 0} mailbox(es) selected</span>
                  {perms.canAdd && <button className="acc-btn" disabled={distBusy} onClick={() => setShowAddMailbox(true)} title="Connect a brand-new mailbox for this brand, then add it to the pool">+ Connect a new mailbox</button>}
                  {perms.canEdit && <button className="acc-btn acc-btn-primary" disabled={distBusy || !(poolSel && poolSel.size)} onClick={distribute} title="Re-pin active contacts across the selected mailboxes so the load is shared and new boxes start dripping">
                    {distBusy ? 'Distributing…' : `Distribute across ${poolSel ? poolSel.size : 0} mailbox(es)`}
                  </button>}
                </div>
              </div>
            </div>
          )}
          {showAddMailbox && seq && (
            <AddMailboxModal
              initial={{ brand_id: String(seq.brand_id || '') }}
              onClose={() => setShowAddMailbox(false)}
              onSaved={async () => { setShowAddMailbox(false); await loadAccounts(); }}
            />
          )}
          {stepModal && seq && steps[stepModal.index] && (
            <EmailStepModal
              brandId={String(seq.brand_id)}
              subject={steps[stepModal.index].subject || ''}
              initialHtml={stepHtml(steps[stepModal.index])}
              initialMode={stepModal.mode}
              onPersist={(v) => persistStepHtml(stepModal.index, v)}
              onClose={() => setStepModal(null)}
            />
          )}
        </>
      )}
    </SidePanel>
  );
}

// BrandEnvelopeEditor — the per-brand editable HTML email SHELL that wraps
// every marketing send. Edit the HTML, insert live widget tokens (logo,
// social, sender, address, unsubscribe), and preview it rendered with the
// brand's real identity. Backed by /api/email/templates/{envelope,widgets,preview}.
function BrandEnvelopeEditor({ onClose }) {
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState('');
  const [widgets, setWidgets] = useState([]);
  const [html, setHtml] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null);
  const taRef = React.useRef(null);

  useEffect(() => {
    get('/brands', false).then((r) => setBrands(r?.brands || r?.data || r || [])).catch(() => {});
    get('/api/email/templates/widgets', false).then((r) => setWidgets(r?.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async (bid) => {
    if (!bid) { setHtml(''); setPreviewHtml(''); return; }
    setBusy(true); setErr(null); setOk(null);
    try {
      const r = await get(`/api/email/templates/envelope?brand_id=${bid}`, false);
      setHtml((r?.data && r.data.bodyHtml) || '');
    } catch (e) { setErr((e && e.message) || 'Load failed'); } finally { setBusy(false); }
  }, []);

  const doPreview = useCallback(async () => {
    if (!brandId || !html) return;
    try {
      const r = await post('/api/email/templates/preview', { brand_id: brandId, envelopeHtml: html }, false);
      setPreviewHtml((r?.data && r.data.html) || '');
    } catch (e) { /* keep last preview */ }
  }, [brandId, html]);
  useEffect(() => { const t = setTimeout(doPreview, 500); return () => clearTimeout(t); }, [doPreview]);

  const insertToken = (token) => {
    const ta = taRef.current;
    if (!ta) { setHtml((h) => h + token); return; }
    const s = ta.selectionStart, e = ta.selectionEnd;
    setHtml((h) => h.slice(0, s) + token + h.slice(e));
    setTimeout(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = s + token.length; }, 0);
  };

  const save = async () => {
    setBusy(true); setErr(null); setOk(null);
    try {
      const { putGeneral } = await import('../../api');
      await putGeneral('/api/email/templates/envelope', { brand_id: brandId, bodyHtml: html });
      setOk('Saved. Every marketing email for this brand now uses this envelope.');
    } catch (e) { setErr((e && (e.response?.data?.error || e.message)) || 'Save failed'); } finally { setBusy(false); }
  };

  return (
    <SidePanel title="Brand email envelope" onClose={onClose} wide>
      <p style={{ fontSize: 12, color: '#667085', marginTop: 0 }}>
        The HTML shell wrapped around every marketing email. Widget tokens render this brand's real
        logo, social links, sender, mailing address, and unsubscribe at send time.
      </p>
      {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
      {ok && <div className="alert alert-success" style={{ fontSize: 13 }}>{ok}</div>}
      <label style={{ display: 'block', fontSize: 11, color: '#525252', marginBottom: 8 }}>Brand
        <select value={brandId} onChange={(e) => { setBrandId(e.target.value); load(e.target.value); }} style={{ width: '100%', fontSize: 12, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4 }}>
          <option value="">Select a brand…</option>
          {brands.map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
        </select>
      </label>
      {brandId && (
        <>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', margin: '6px 0 8px' }}>
            <span style={{ fontSize: 10, color: '#9ca3af', alignSelf: 'center' }}>Insert widget:</span>
            {widgets.map((w) => (
              <button key={w.token} className="acc-btn" style={{ fontSize: 10, padding: '2px 6px' }} onClick={() => insertToken(w.token)}>{w.label}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <textarea ref={taRef} value={html} onChange={(e) => setHtml(e.target.value)} spellCheck={false}
              style={{ flex: '1 1 320px', minHeight: 340, fontSize: 12, padding: 8, border: '1px solid #ddd', borderRadius: 4, fontFamily: 'monospace' }} />
            <div style={{ flex: '1 1 320px', border: '1px solid #ddd', borderRadius: 6, overflow: 'hidden', minHeight: 340 }}>
              <iframe sandbox="allow-same-origin" srcDoc={previewHtml || '<div style="padding:20px;font-family:sans-serif;color:#9ca3af">Preview…</div>'} style={{ width: '100%', height: 340, border: 'none' }} title="Envelope preview" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <button className="acc-btn" disabled={busy} onClick={() => load(brandId)}>Reset</button>
            <button className="acc-btn acc-btn-primary" disabled={busy || !html.includes('{{body}}') || !html.includes('{{widget.unsubscribe}}')} onClick={save}>{busy ? 'Saving…' : 'Save envelope'}</button>
          </div>
          {(!html.includes('{{body}}') || !html.includes('{{widget.unsubscribe}}')) && (
            <div style={{ fontSize: 11, color: '#b45309', marginTop: 6 }}>The template must keep <code>{'{{body}}'}</code> and <code>{'{{widget.unsubscribe}}'}</code> (unsubscribe is legally required).</div>
          )}
        </>
      )}
    </SidePanel>
  );
}

// TemplateDetailPanel — template row click. Preview / edit (save-override)
// / clone a single template. Takes the row object (the list already has
// all fields).
function TemplateDetailPanel({ tpl, perms, onClose, onChanged }) {
  const [name, setName] = useState(tpl.name || '');
  const [subject, setSubject] = useState(tpl.subject || '');
  const [preheader, setPreheader] = useState(tpl.preheader || '');
  const [fromName, setFromName] = useState(tpl.fromName || '');
  const [bodyText, setBodyText] = useState(tpl.bodyText || '');
  const [bodyHtml, setBodyHtml] = useState(tpl.bodyHtml || '');
  const [previewOn, setPreviewOn] = useState(true);
  const [tab, setTab] = useState('html');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const save = async () => {
    setBusy(true); setErr(null);
    try { const { putGeneral } = await import('../../api'); await putGeneral(`/api/email/templates/${tpl._id}`, { name, subject, preheader, fromName, bodyText, bodyHtml }); onChanged && onChanged(); onClose && onClose(); }
    catch (e) { setErr((e && e.message) || 'Save failed'); } finally { setBusy(false); }
  };
  const clone = async () => {
    setBusy(true); setErr(null);
    try { await post(`/api/email/templates/${tpl._id}/clone`, {}, false); window.alert('Cloned.'); onChanged && onChanged(); onClose && onClose(); }
    catch (e) { setErr((e && e.message) || 'Clone failed'); } finally { setBusy(false); }
  };

  return (
    <SidePanel title={tpl.name || 'Template'} onClose={onClose}>
      {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 4 }}>
        <span style={{ fontSize: 11, color: '#888' }}>{tpl.kind}{tpl.crmAction ? ` · ${tpl.crmAction}` : ''}{tpl.listType ? ` · ${tpl.listType}` : ''}</span>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {previewOn && (
            <div style={{ display: 'flex', gap: 2, fontSize: 11, background: '#eee', borderRadius: 4, padding: 2 }}>
              <button className={`btn btn-sm m-0 ${tab === 'html' ? 'btn-primary' : 'btn-outline-secondary'}`} style={{ fontSize: 10, padding: '2px 6px' }} onClick={() => setTab('html')}>HTML</button>
              <button className={`btn btn-sm m-0 ${tab === 'text' ? 'btn-primary' : 'btn-outline-secondary'}`} style={{ fontSize: 10, padding: '2px 6px' }} onClick={() => setTab('text')}>Text</button>
            </div>
          )}
          <label style={{ fontSize: 11, color: '#525252', display: 'flex', gap: 4, alignItems: 'center' }}><input type="checkbox" checked={previewOn} onChange={(e) => setPreviewOn(e.target.checked)} /> Edit</label>
        </div>
      </div>
      {previewOn ? (
        tab === 'html' ? (
          <div style={{ border: '1px solid #ddd', borderRadius: 6, overflow: 'hidden' }}>
            <iframe sandbox="allow-same-origin" srcDoc={bodyHtml || `<div style="padding:20px;font-family:sans-serif;font-size:14px;color:#374151">${(bodyText||'').replace(/\n/g,'<br>')}</div>`} style={{ width: '100%', height: 380, border: 'none' }} title="Email preview" />
          </div>
        ) : (
          <div style={{ background: '#fafbfc', border: '1px solid #eee', borderRadius: 6, padding: 12 }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{renderPreview(subject) || '(no subject)'}</div>
            {preheader && <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6 }}>{renderPreview(preheader)}</div>}
            {fromName && <div style={{ fontSize: 11, color: '#667085', marginBottom: 4 }}>From: {fromName}</div>}
            <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{renderPreview(bodyText) || '(empty)'}</div>
          </div>
        )
      ) : (
        <>
          <label style={{ display: 'block', fontSize: 11, color: '#525252', marginBottom: 8 }}>Name
            <input value={name} onChange={(e) => setName(e.target.value)} style={{ width: '100%', fontSize: 12, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4 }} /></label>
          <label style={{ display: 'block', fontSize: 11, color: '#525252', marginBottom: 8 }}>From name
            <input value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="Sender display name" style={{ width: '100%', fontSize: 12, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4 }} /></label>
          <label style={{ display: 'block', fontSize: 11, color: '#525252', marginBottom: 8 }}>Subject
            <input value={subject} onChange={(e) => setSubject(e.target.value)} style={{ width: '100%', fontSize: 12, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4 }} /></label>
          <label style={{ display: 'block', fontSize: 11, color: '#525252', marginBottom: 8 }}>Preheader
            <input value={preheader} onChange={(e) => setPreheader(e.target.value)} placeholder="Inbox preview text" style={{ width: '100%', fontSize: 12, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4 }} /></label>
          <label style={{ display: 'block', fontSize: 11, color: '#525252', marginBottom: 8 }}>Body text
            <textarea value={bodyText} onChange={(e) => setBodyText(e.target.value)} rows={10} style={{ width: '100%', fontSize: 13, padding: 8, border: '1px solid #ddd', borderRadius: 4, fontFamily: 'inherit' }} /></label>
        </>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
        {previewOn && bodyHtml && bodyHtml.includes('<!DOCTYPE') && (
          <button className="acc-btn" onClick={() => { const w = window.open('', '_blank'); w.document.write(bodyHtml); w.document.close(); }}>Open preview</button>
        )}
        {perms.canAdd && <button className="acc-btn" disabled={busy} onClick={clone}>Clone</button>}
        {perms.canEdit && <button className="acc-btn acc-btn-primary" disabled={busy} onClick={save}>Save</button>}
      </div>
    </SidePanel>
  );
}

// GenerateTemplateModal — AI-draft a reusable template for a brand. The
// worker LLM-drafts subject+body in the brand voice then polishes it; the
// new template shows up in the Templates list on the next refresh.
function GenerateTemplateModal({ onClose, onQueued }) {
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState('');
  const [kind, setKind] = useState('campaign');
  const [goal, setGoal] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => { (async () => {
    try { const r = await get('/brands'); setBrands((r && r.data) || []); }
    catch (e) { setErr((e && e.message) || 'Failed to load brands'); }
  })(); }, []);

  const submit = async () => {
    if (!brandId) return setErr('Pick a brand');
    setBusy(true); setErr(null);
    try {
      await post('/api/email/templates/generate', { brand_id: brandId, kind, goal }, false);
      onQueued && onQueued();
    } catch (e) { setErr((e && e.message) || 'Generate failed'); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', width: 560, maxWidth: '92vw', borderRadius: 8, padding: 20 }} onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-baseline justify-content-between mb-3">
          <h5 className="mb-0">Generate email template</h5>
          <button className="acc-btn" onClick={onClose}>Close</button>
        </div>
        <p className="em-stub mb-3">The AI drafts a brand-voiced template (subject + body) and polishes it. It appears in the Templates list, ready to use in a campaign or sequence.</p>
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, color: '#525252', gap: 2, marginBottom: 10 }}>
          <span>Brand *</span>
          <select value={brandId} onChange={(e) => setBrandId(e.target.value)} style={{ fontSize: 12, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4 }}>
            <option value="">— pick brand —</option>
            {brands.map((b) => (<option key={b._id} value={b._id}>{b.name}</option>))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, color: '#525252', gap: 2, marginBottom: 10 }}>
          <span>Kind</span>
          <select value={kind} onChange={(e) => setKind(e.target.value)} style={{ fontSize: 12, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4 }}>
            {['campaign', 'autoreply', 'transactional', 'crm'].map((k) => (<option key={k} value={k}>{k}</option>))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, color: '#525252', gap: 2, marginBottom: 10 }}>
          <span>Goal / what it's for (optional)</span>
          <textarea value={goal} onChange={(e) => setGoal(e.target.value)} rows={3} placeholder="e.g. Re-engage trial users who went quiet" style={{ fontSize: 12, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4 }} />
        </label>
        {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="acc-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="acc-btn acc-btn-primary" onClick={submit} disabled={busy}>{busy ? 'Queuing…' : 'Generate'}</button>
        </div>
      </div>
    </div>
  );
}

// CrmSourceModal — add/edit an external CRM source connector. Handles the
// JSON config fields (queryParams / fieldMapping) by parsing before POST.
function CrmSourceModal({ source, onClose, onSaved }) {
  const editing = !!(source && source._id);
  const [brands, setBrands] = useState([]);
  const [f, setF] = useState({
    name: source?.name || '', kind: source?.kind || 'rest-api', category: source?.category || 'cold-prospect',
    brand_id: source?.brand_id || '', baseUrl: source?.baseUrl || '', usersEndpoint: source?.usersEndpoint || '',
    authType: source?.authType || 'none', authHeaderName: source?.authHeaderName || '', apiToken: '',
    wpUsername: source?.wpUsername || '', dataPath: source?.dataPath || '', filePath: source?.filePath || '',
    queryParams: JSON.stringify(source?.queryParams || {}, null, 0), fieldMapping: JSON.stringify(source?.fieldMapping || {}, null, 0),
    maxRecords: source?.maxRecords ?? 5000, rampBase: source?.rampBase ?? 5000, syncIntervalMinutes: source?.syncIntervalMinutes ?? 1440,
    enabled: source?.enabled !== false,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  useEffect(() => { (async () => { try { const r = await get('/brands'); setBrands((r && r.data) || []); } catch (_) {} })(); }, []);

  const submit = async () => {
    if (!f.name || !f.kind) return setErr('Name + kind required');
    let queryParams, fieldMapping;
    try { queryParams = JSON.parse(f.queryParams || '{}'); fieldMapping = JSON.parse(f.fieldMapping || '{}'); }
    catch (e) { return setErr('queryParams / fieldMapping must be valid JSON'); }
    setBusy(true); setErr(null);
    const body = { ...f, queryParams, fieldMapping, maxRecords: Number(f.maxRecords), rampBase: Number(f.rampBase), syncIntervalMinutes: Number(f.syncIntervalMinutes) };
    if (!body.apiToken) delete body.apiToken; // empty = keep saved secret
    try {
      const { putGeneral } = await import('../../api');
      if (editing) await putGeneral(`/api/crm-sources/${source._id}`, body);
      else await post('/api/crm-sources', body, false);
      onSaved && onSaved();
    } catch (e) { setErr((e && e.message) || 'Save failed'); } finally { setBusy(false); }
  };
  const fld = (label, k, type = 'text') => (
    <label style={{ display: 'block', fontSize: 11, color: '#525252', marginBottom: 8 }}>{label}
      <input type={type} value={f[k]} onChange={(e) => set(k, e.target.value)} style={{ width: '100%', fontSize: 12, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4 }} /></label>
  );
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', width: 640, maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto', borderRadius: 8, padding: 20 }} onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-baseline justify-content-between mb-3"><h5 className="mb-0">{editing ? 'Edit' : 'New'} CRM source</h5><button className="acc-btn" onClick={onClose}>Close</button></div>
        {fld('Name *', 'name')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <label style={{ fontSize: 11, color: '#525252' }}>Kind<select value={f.kind} onChange={(e) => set('kind', e.target.value)} style={{ width: '100%', fontSize: 12, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4 }}>{['rest-api', 'wordpress', 'parquet-file', 'csv-file', 'custom'].map((k) => <option key={k}>{k}</option>)}</select></label>
          <label style={{ fontSize: 11, color: '#525252' }}>Category (placement)<select value={f.category} onChange={(e) => set('category', e.target.value)} style={{ width: '100%', fontSize: 12, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4 }}>{['cold-prospect', 'warm-prospect', 'active-customer', 'subscribed-customer'].map((k) => <option key={k}>{k}</option>)}</select></label>
        </div>
        <label style={{ display: 'block', fontSize: 11, color: '#525252', margin: '8px 0' }}>Brand<select value={f.brand_id} onChange={(e) => set('brand_id', e.target.value)} style={{ width: '100%', fontSize: 12, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4 }}><option value="">— pick brand —</option>{brands.map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}</select></label>
        {f.kind === 'parquet-file' || f.kind === 'csv-file' ? fld('File path (on server)', 'filePath') : (<>
          {fld('Base URL', 'baseUrl')}
          {fld('Users endpoint', 'usersEndpoint')}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <label style={{ fontSize: 11, color: '#525252' }}>Auth<select value={f.authType} onChange={(e) => set('authType', e.target.value)} style={{ width: '100%', fontSize: 12, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4 }}>{['none', 'bearer', 'header', 'basic'].map((k) => <option key={k}>{k}</option>)}</select></label>
            {f.authType === 'header' ? fld('Header name', 'authHeaderName') : <div />}
          </div>
          {fld(`Token / secret ${editing ? '(blank = keep)' : ''}`, 'apiToken', 'password')}
          {f.kind === 'wordpress' && fld('WP username', 'wpUsername')}
          {fld('Data path (array key, e.g. rows)', 'dataPath')}
        </>)}
        <label style={{ display: 'block', fontSize: 11, color: '#525252', marginBottom: 8 }}>Query params (JSON filter)<textarea value={f.queryParams} onChange={(e) => set('queryParams', e.target.value)} rows={2} placeholder='{"service":"bidlight","country":"united states","role":"cxo,owner"}' style={{ width: '100%', fontSize: 12, padding: 6, border: '1px solid #ddd', borderRadius: 4 }} /></label>
        <label style={{ display: 'block', fontSize: 11, color: '#525252', marginBottom: 8 }}>Field mapping (JSON)<textarea value={f.fieldMapping} onChange={(e) => set('fieldMapping', e.target.value)} rows={2} placeholder='{"firstName":"first_name","title":"job_title"}' style={{ width: '100%', fontSize: 12, padding: 6, border: '1px solid #ddd', borderRadius: 4 }} /></label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          {fld('Max/run', 'maxRecords', 'number')}{fld('Ramp base', 'rampBase', 'number')}{fld('Sync mins', 'syncIntervalMinutes', 'number')}
        </div>
        <label style={{ fontSize: 12, color: '#525252', display: 'flex', gap: 6, margin: '4px 0 10px' }}><input type="checkbox" checked={f.enabled} onChange={(e) => set('enabled', e.target.checked)} /> Enabled</label>
        {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}><button className="acc-btn" onClick={onClose} disabled={busy}>Cancel</button><button className="acc-btn acc-btn-primary" onClick={submit} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button></div>
      </div>
    </div>
  );
}

// CrmSourceDetailPanel — source row click. Test, Sync now, edit, stats.
function CrmSourceDetailPanel({ source, perms, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [editing, setEditing] = useState(false);
  const act = async (fn, label) => { setBusy(true); setMsg(null); try { const r = await fn(); setMsg(`${label}: ok${r && r.data && r.data.total != null ? ` (source total ${r.data.total})` : ''}`); onChanged && onChanged(); } catch (e) { setMsg(`${label}: ${(e && e.message) || 'failed'}`); } finally { setBusy(false); } };
  if (editing) return <CrmSourceModal source={source} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); onChanged && onChanged(); onClose && onClose(); }} />;
  const s = source;
  return (
    <SidePanel title={s.name} onClose={onClose}>
      <div style={{ fontSize: 12, color: '#525252', marginBottom: 10 }}>
        <div><strong>{s.kind}</strong> · {s.category} · {s.enabled ? 'enabled' : 'disabled'}</div>
        {s.baseUrl && <div style={{ marginTop: 4 }}>{s.baseUrl}{s.usersEndpoint}</div>}
        {s.queryParams && <div style={{ marginTop: 4, color: '#888' }}>filter: {JSON.stringify(s.queryParams)}</div>}
        <div style={{ marginTop: 6 }}>Pulled: {s.backfillOffset || 0}{s.sourceTotal ? ` / ${s.sourceTotal}` : ''} · runs: {s.backfillRunCount || 0}{s.backfillComplete ? ' · complete' : ''}</div>
        {s.lastSyncStats && <div style={{ color: '#888' }}>last: {JSON.stringify(s.lastSyncStats)}</div>}
        {s.lastSyncError && <div style={{ color: '#a71d2a' }}>error: {s.lastSyncError}</div>}
      </div>
      {msg && <div className="alert alert-info" style={{ fontSize: 12 }}>{msg}</div>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {perms.canEdit && <button className="acc-btn" disabled={busy} onClick={() => act(() => post(`/api/crm-sources/${s._id}/test`, {}, true), 'Test')}>Test</button>}
        {perms.canSync && <button className="acc-btn acc-btn-primary" disabled={busy} onClick={() => act(() => post(`/api/crm-sources/${s._id}/sync-now`, {}, false), 'Sync now')}>Sync now</button>}
        {perms.canEdit && <button className="acc-btn" disabled={busy} onClick={() => setEditing(true)}>Edit</button>}
        {perms.canDelete && <button className="acc-btn" disabled={busy} onClick={async () => { if (!window.confirm('Delete this source?')) return; const { remove } = await import('../../api'); try { await remove(`/api/crm-sources/${s._id}`); onChanged && onChanged(); onClose && onClose(); } catch (e) { setMsg((e && e.message) || 'delete failed'); } }}>Delete</button>}
      </div>
    </SidePanel>
  );
}

function getPath(obj, path) {
  return path.split('.').reduce((o, k) => (o == null ? null : o[k]), obj);
}
function fmtCell(v) {
  if (v == null || v === '') return '—';
  if (typeof v === 'boolean') return v ? '✓' : '—';
  if (v instanceof Date) return v.toLocaleString();
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) { try { return new Date(v).toLocaleString(); } catch {} }
  return String(v);
}

export default function EmailManagement() {
  const userInfoRedux = useSelector((s) => s.user.userInfo);
  const usersPermissionRedux   = useSelector((s) => s.user.userPermission);
  const companyPermissionRedux = useSelector((s) => s.user.companyPermission);
  const allPermissionRedux     = useSelector((s) => s.user.allPermission);
  const FinalPermissions = useMemo(() => (
    (allPermissionRedux || [])
      .filter((p) => (companyPermissionRedux || []).includes(p))
      .filter((p) => (usersPermissionRedux  || []).includes(p))
  ), [allPermissionRedux, companyPermissionRedux, usersPermissionRedux]);
  const isSuperAdmin = !!userInfoRedux?.superAdmin;

  // Tab is driven by the URL :tab param — so each tab is deep-linkable
  // and the sidebar can link straight to /email-management/campaigns,
  // /email-management/contacts, etc. (mirrors the Social Posts pattern).
  const { tab: urlTab } = useParams();
  const history = useHistory();
  const visibleTabs = TABS.filter((t) => isSuperAdmin || FinalPermissions.includes(ALL_PERMISSIONS[t.perm]));
  // Resolve current tab: URL → first visible. setTab pushes the new URL
  // so the back button + bookmarks work naturally.
  const tab = (TABS.find((t) => t.key === urlTab) ? urlTab : (visibleTabs[0]?.key || 'inbox'));
  const setTab = (k) => history.push(`/email-management/${k}`);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Header filter — brand + date (today / last week / last month / custom).
  const [filters, setFilters] = useState(EMPTY_SYSTEM_FILTERS);
  const brands = useBrandList();
  const [showAddMailbox, setShowAddMailbox] = useState(false);
  const canAddMailbox = isSuperAdmin || FinalPermissions.includes(ALL_PERMISSIONS.EMAIL_ACCOUNT_ADD);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedSequence, setSelectedSequence] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showGenerateSeq, setShowGenerateSeq] = useState(false);
  const [showGenerateTpl, setShowGenerateTpl] = useState(false);
  const [showEnvelope, setShowEnvelope] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [showAddSource, setShowAddSource] = useState(false);
  const crmSourcePerms = {
    canAdd:    isSuperAdmin || FinalPermissions.includes(ALL_PERMISSIONS.CRM_SOURCE_ADD),
    canEdit:   isSuperAdmin || FinalPermissions.includes(ALL_PERMISSIONS.CRM_SOURCE_EDIT),
    canDelete: isSuperAdmin || FinalPermissions.includes(ALL_PERMISSIONS.CRM_SOURCE_DELETE),
    canSync:   isSuperAdmin || FinalPermissions.includes(ALL_PERMISSIONS.CRM_SOURCE_SYNC),
  };
  const tplPerms = {
    canAdd:  isSuperAdmin || FinalPermissions.includes(ALL_PERMISSIONS.EMAIL_TEMPLATE_ADD),
    canEdit: isSuperAdmin || FinalPermissions.includes(ALL_PERMISSIONS.EMAIL_TEMPLATE_EDIT),
  };
  const canSend = isSuperAdmin || FinalPermissions.includes(ALL_PERMISSIONS.EMAIL_MESSAGE_SEND);
  const seqPerms = {
    canAdd:    isSuperAdmin || FinalPermissions.includes(ALL_PERMISSIONS.EMAIL_SEQUENCE_ADD),
    canEdit:   isSuperAdmin || FinalPermissions.includes(ALL_PERMISSIONS.EMAIL_SEQUENCE_EDIT),
    canLaunch: isSuperAdmin || FinalPermissions.includes(ALL_PERMISSIONS.EMAIL_SEQUENCE_LAUNCH),
  };
  const canGenerateSeq = isSuperAdmin || FinalPermissions.includes(ALL_PERMISSIONS.EMAIL_SEQUENCE_GENERATE);
  // Generic create modal — null when closed, set to a tab key to open.
  const [createTab, setCreateTab] = useState(null);
  // Inbox compose ("new email", not "reply").
  const [showCompose, setShowCompose] = useState(false);
  // CSV import for the contacts tab.
  const [showCsvImport, setShowCsvImport] = useState(false);
  // Contact selection + per-row action menu + bulk/single action modal.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [openMenuId, setOpenMenuId] = useState(null);
  const [contactAction, setContactAction] = useState(null); // { type:'edit'|'assign-brand'|'assign-list', ids:[], contact? }
  useEffect(() => { setSelectedIds(new Set()); setOpenMenuId(null); }, [tab]);
  const toggleSelect = (id) => setSelectedIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const activeTab = TABS.find((t) => t.key === tab);

  // ── Pagination + hybrid search ──────────────────────────────────────────
  // Contacts is server-paginated (150k-600k rows); other tabs are small so
  // they load once and paginate client-side. Search: filters the loaded page
  // instantly (local) AND, for contacts, fires a debounced BACKEND full-set
  // search that streams the remaining matches in (shown as "searching all…").
  const SERVER_PAGINATED = tab === 'contacts';
  const PAGE_SIZE = 100;
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState('');
  const [searchingAll, setSearchingAll] = useState(false);
  const [backendMatches, setBackendMatches] = useState(null); // contacts: full-set search results
  useEffect(() => { setPage(1); setQuery(''); setBackendMatches(null); }, [tab]);

  const refresh = useCallback(async () => {
    if (!activeTab) return;
    // A self-contained tab fetches its own data; there is no endpoint here to
    // call, and calling one would only produce rows the shared table cannot
    // draw.
    if (!activeTab.endpoint) { setRows([]); setTotal(0); setBusy(false); return; }
    setBusy(true); setError(null);
    try {
      const sep = activeTab.endpoint.includes('?') ? '&' : '?';
      const url = SERVER_PAGINATED ? `${activeTab.endpoint}${sep}page=${page}&limit=${PAGE_SIZE}` : activeTab.endpoint;
      const r = await get(url);
      setRows(((r && r.data) || []));
      if (r && typeof r.total === 'number') setTotal(r.total);
      else setTotal(((r && r.data) || []).length);
    } catch (e) { setError((e && e.message) || 'Failed to load'); setRows([]); }
    finally { setBusy(false); }
  }, [activeTab, SERVER_PAGINATED, page]);

  useEffect(() => { refresh(); }, [refresh]);

  // Hybrid backend search for contacts — debounced; merges full-set matches.
  useEffect(() => {
    if (!SERVER_PAGINATED) { setBackendMatches(null); setSearchingAll(false); return; }
    const q = query.trim();
    if (!q) { setBackendMatches(null); setSearchingAll(false); return; }
    let dead = false;
    setSearchingAll(true);
    const t = setTimeout(async () => {
      try {
        const r = await get(`${activeTab.endpoint}?search=${encodeURIComponent(q)}&limit=500&page=1`);
        if (!dead) { setBackendMatches((r && r.data) || []); setTotal((r && r.total) || 0); }
      } catch (_) { if (!dead) setBackendMatches([]); }
      finally { if (!dead) setSearchingAll(false); }
    }, 350);
    return () => { dead = true; clearTimeout(t); };
  }, [query, SERVER_PAGINATED, activeTab]);

  // Kick the Microsoft 365 OAuth consent flow for a connector row.
  const connectM365 = async (id) => {
    try {
      const r = await get(`/api/email/oauth/microsoft/start?accountId=${id}`);
      const url = r && r.data && r.data.url;
      if (url) window.location.href = url; else window.alert('No consent URL returned');
    } catch (e) { window.alert((e && e.message) || 'Microsoft connect failed'); }
  };

  // Auto-refresh the active tab every 30s. Cheap polling — covers the
  // inbox catching newly-ingested messages, campaign stats updating as
  // sends fan out, etc. Faster than socket.io for the scope we need.
  useEffect(() => {
    const t = setInterval(() => { refresh(); }, 30_000);
    return () => clearInterval(t);
  }, [refresh]);

  // Decide what the tab header "+ New" button does. Inbox = compose,
  // accounts = AddMailbox, contacts = create OR import CSV, everything
  // else = generic CreateModal.
  const createCfg = CREATE_CONFIG[tab];
  const canCreate = createCfg && (isSuperAdmin || FinalPermissions.includes(ALL_PERMISSIONS[createCfg.perm]));

  if (visibleTabs.length === 0) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning">
          Your company doesn't have the Email Management package enabled. Ask a super admin to add it.
        </div>
      </div>
    );
  }

  const cols = COLUMNS[tab] || [];
  // Local (instant) text filter over the loaded rows — searches every string
  // field so results appear the moment you type, before the backend replies.
  const matchesQuery = (r, q) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return Object.values(r).some((v) => typeof v === 'string' && v.toLowerCase().includes(needle))
      || (Array.isArray(r.tags) && r.tags.some((t) => String(t).toLowerCase().includes(needle)));
  };
  const q = query.trim();
  let baseRows = rows;
  if (q) {
    const local = rows.filter((r) => matchesQuery(r, q));
    if (SERVER_PAGINATED && backendMatches) {
      // Merge: local hits first (instant), then any backend-only matches.
      const seen = new Set(local.map((r) => r._id));
      baseRows = [...local, ...backendMatches.filter((r) => !seen.has(r._id))];
    } else {
      baseRows = local;
    }
  }
  const visibleRows = applySystemFilter(baseRows, filters);
  // Client-side pagination for the non-server-paginated tabs.
  const clientPaged = !SERVER_PAGINATED && !q;
  const pageCount = clientPaged ? Math.max(1, Math.ceil(visibleRows.length / PAGE_SIZE)) : 1;
  const pagedRows = clientPaged ? visibleRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) : visibleRows;
  return (
    <div className="container py-4">
      <style>{`
        .em-tabs { display: flex; gap: 4px; border-bottom: 1px solid #e5e5e5; margin-bottom: 16px; }
        .em-tab { padding: 8px 14px; cursor: pointer; border: 1px solid transparent; border-bottom: none; border-radius: 6px 6px 0 0; font-size: 13px; color: #525252; }
        .em-tab.active { background: #fff; border-color: #e5e5e5; color: #15172b; font-weight: 600; }
        .em-card { background: #fff; border: 1px solid #e5e5e5; border-radius: 8px; padding: 14px; }
        .em-table { width: 100%; font-size: 13px; }
        .em-table th { font-size: 11px; color: #888; text-transform: uppercase; text-align: left; font-weight: 500; border-bottom: 1px solid #f1f1f1; padding: 6px 8px; }
        .em-table td { padding: 6px 8px; border-bottom: 1px solid #f4f4f4; }
        .em-empty { color: #999; padding: 16px; text-align: center; }
        .em-stub { font-size: 12px; color: #888; }
        .acc-btn { font-size: 12px; padding: 4px 10px; border: 1px solid #ddd; background: #fff; border-radius: 4px; cursor: pointer; color: #525252; }
        .acc-btn:hover:not(:disabled) { background: #f7f9fc; border-color: #bbb; }
        .acc-btn:disabled { opacity: .5; cursor: wait; }
        .acc-btn-primary { color: #fff; background: #0F62FE; border-color: #0F62FE; }
        .acc-btn-primary:hover:not(:disabled) { background: #0846b3; }
      `}</style>

      <div className="d-flex align-items-baseline justify-content-between mb-2">
        <h3 className="mb-0">Email Management</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {tab === 'accounts' && canAddMailbox && (
            <button className="acc-btn acc-btn-primary" onClick={() => setShowAddMailbox(true)}>+ Connect mailbox</button>
          )}
          {tab === 'inbox' && canSend && (
            <button className="acc-btn acc-btn-primary" onClick={() => setShowCompose(true)}>+ Compose</button>
          )}
          {tab === 'sequences' && canGenerateSeq && (
            <button className="acc-btn acc-btn-primary" onClick={() => setShowGenerateSeq(true)}>+ Generate sequence</button>
          )}
          {tab === 'templates' && (isSuperAdmin || FinalPermissions.includes(ALL_PERMISSIONS.EMAIL_TEMPLATE_ADD)) && (
            <>
              <button className="acc-btn" onClick={() => setShowEnvelope(true)}>✉️ Brand envelope</button>
              <button className="acc-btn" onClick={() => setShowGenerateTpl(true)}>✨ Generate</button>
            </>
          )}
          {tab === 'crmsources' && crmSourcePerms.canAdd && (
            <button className="acc-btn acc-btn-primary" onClick={() => setShowAddSource(true)}>+ Add source</button>
          )}
          {tab === 'contacts' && (isSuperAdmin || FinalPermissions.includes(ALL_PERMISSIONS.EMAIL_CONTACT_IMPORT)) && (
            <button className="acc-btn" onClick={() => setShowCsvImport(true)}>Import CSV</button>
          )}
          {canCreate && tab !== 'accounts' && tab !== 'inbox' && (
            <button className="acc-btn acc-btn-primary" onClick={() => setCreateTab(tab)}>+ New {createCfg.title.replace(/^New\s+/i, '')}</button>
          )}
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Search ${activeTab.label.toLowerCase()}…`}
            style={{ fontSize: 12, padding: '5px 10px', border: '1px solid #ddd', borderRadius: 4, minWidth: 180 }} />
          <div className="em-stub">
            {SERVER_PAGINATED
              ? `${(total || 0).toLocaleString()} total`
              : `${visibleRows.length} record${visibleRows.length === 1 ? '' : 's'}`}
            {searchingAll && <span style={{ color: '#856404', marginLeft: 6 }}>· searching all…</span>}
            {' · auto-refresh 30s'}
          </div>
        </div>
      </div>
      {showEnvelope && <BrandEnvelopeEditor onClose={() => setShowEnvelope(false)} />}
      {showAddMailbox && <AddMailboxModal onClose={() => setShowAddMailbox(false)} onSaved={() => { setShowAddMailbox(false); refresh(); }} />}
      {showCompose && <ComposeModal onClose={() => setShowCompose(false)} onSent={() => { setShowCompose(false); refresh(); }} />}
      {showCsvImport && <CsvImportModal onClose={() => setShowCsvImport(false)} onImported={() => { setShowCsvImport(false); refresh(); }} />}
      {contactAction && <ContactActionModal action={contactAction} onClose={() => setContactAction(null)} onDone={() => { setContactAction(null); setSelectedIds(new Set()); refresh(); }} />}
      {createTab && CREATE_CONFIG[createTab] && (
        <CreateModal {...CREATE_CONFIG[createTab]} onClose={() => setCreateTab(null)} onSaved={() => { setCreateTab(null); refresh(); }} />
      )}
      {selectedMessage && <MessageDetailPanel id={selectedMessage} canSend={canSend} onClose={() => setSelectedMessage(null)} onAction={refresh} />}
      {selectedContact && <ContactActivityPanel id={selectedContact} onClose={() => setSelectedContact(null)} onPickMessage={(mid) => { setSelectedContact(null); setSelectedMessage(mid); }} />}
      {selectedCampaign && <CampaignDetailPanel id={selectedCampaign} onClose={() => setSelectedCampaign(null)} onPickMessage={(mid) => { setSelectedCampaign(null); setSelectedMessage(mid); }} />}
      {selectedAccount && <AccountHealthPanel id={selectedAccount} onClose={() => setSelectedAccount(null)} onChanged={refresh} />}
      {showGenerateSeq && <GenerateSequenceModal onClose={() => setShowGenerateSeq(false)} onQueued={() => { setShowGenerateSeq(false); window.alert('Generating — the sequence will appear here in a few moments. Refresh if needed.'); refresh(); }} />}
      {showGenerateTpl && <GenerateTemplateModal onClose={() => setShowGenerateTpl(false)} onQueued={() => { setShowGenerateTpl(false); window.alert('Generating — the template will appear here in a few moments. Refresh if needed.'); refresh(); }} />}
      {selectedSequence && <SequenceDetailPanel id={selectedSequence} perms={seqPerms} onClose={() => setSelectedSequence(null)} onChanged={refresh} />}
      {selectedTemplate && <TemplateDetailPanel tpl={selectedTemplate} perms={tplPerms} onClose={() => setSelectedTemplate(null)} onChanged={refresh} />}
      {showAddSource && <CrmSourceModal onClose={() => setShowAddSource(false)} onSaved={() => { setShowAddSource(false); refresh(); }} />}
      {selectedSource && <CrmSourceDetailPanel source={selectedSource} perms={crmSourcePerms} onClose={() => setSelectedSource(null)} onChanged={refresh} />}

      <div className="em-tabs">
        {visibleTabs.map((t) => (
          <div key={t.key} className={"em-tab" + (tab === t.key ? " active" : "")} onClick={() => setTab(t.key)}>{t.label}</div>
        ))}
      </div>

      {!SELF_CONTAINED.has(tab) && (
        <SystemFilterBar
          filters={filters}
          setFilters={setFilters}
          brands={brands}
          resultCount={visibleRows.length}
          totalCount={rows.length}
        />
      )}

      {error && <div className="alert alert-warning" style={{ fontSize: 13 }}>{error}</div>}

      <div className="em-card">
        {SELF_CONTAINED.has(tab) && (
          <SignaturesTab
            brands={brands}
            canEdit={isSuperAdmin || FinalPermissions.includes(ALL_PERMISSIONS.EMAIL_ACCOUNT_EDIT)}
          />
        )}
        {!SELF_CONTAINED.has(tab) && busy && <div className="em-empty">Loading…</div>}
        {!SELF_CONTAINED.has(tab) && !busy && rows.length === 0 && (
          <div className="em-empty">
            No {activeTab.label.toLowerCase()} yet.
            {tab === 'accounts' && <div className="em-stub mt-2">Connect a mailbox to get started. The "Add mailbox" wizard ships in Phase 2.</div>}
          </div>
        )}
        {!busy && rows.length > 0 && visibleRows.length === 0 && (
          <div className="em-empty">No {activeTab.label.toLowerCase()} match the current filters.</div>
        )}
        {tab === 'contacts' && selectedIds.size > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', background: '#eef2ff', border: '1px solid #dfe3f5', borderRadius: 6, padding: '8px 12px', marginBottom: 8 }}>
            <b style={{ fontSize: 12 }}>{selectedIds.size} selected</b>
            <button className="acc-btn" style={{ fontSize: 12 }} onClick={() => setContactAction({ type: 'assign-brand', ids: [...selectedIds] })}>Assign to brand</button>
            <button className="acc-btn" style={{ fontSize: 12 }} onClick={() => setContactAction({ type: 'assign-list', ids: [...selectedIds] })}>Add to list</button>
            <button className="acc-btn" style={{ fontSize: 12 }} onClick={() => setContactAction({ type: 'edit', ids: [...selectedIds] })}>Edit fields</button>
            <button className="acc-btn" style={{ fontSize: 12 }} onClick={async () => { await post('/api/email/contacts/bulk', { ids: [...selectedIds], action: 'unsubscribe', value: true }, false); setSelectedIds(new Set()); refresh(); }}>Unsubscribe</button>
            <button className="acc-btn" style={{ fontSize: 12, color: '#a71d2a' }} onClick={async () => { if (window.confirm(`Delete ${selectedIds.size} contact(s)?`)) { await post('/api/email/contacts/bulk', { ids: [...selectedIds], action: 'delete' }, false); setSelectedIds(new Set()); refresh(); } }}>Delete</button>
            <button className="acc-btn" style={{ fontSize: 12, marginLeft: 'auto' }} onClick={() => setSelectedIds(new Set())}>Clear</button>
          </div>
        )}
        {!busy && visibleRows.length > 0 && (
          <table className="em-table">
            <thead>
              <tr>
                {tab === 'contacts' && (
                  <th style={{ width: 28 }}>
                    <input type="checkbox"
                      checked={pagedRows.length > 0 && pagedRows.every((r) => selectedIds.has(r._id))}
                      ref={(el) => { if (el) el.indeterminate = selectedIds.size > 0 && !pagedRows.every((r) => selectedIds.has(r._id)); }}
                      onChange={(e) => setSelectedIds(e.target.checked ? new Set(pagedRows.map((r) => r._id)) : new Set())} />
                  </th>
                )}
                {cols.map(([key, label]) => (<th key={key}>{label}</th>))}
                {tab === 'accounts' && <th>Connect</th>}
                {tab === 'contacts' && <th style={{ width: 40 }}></th>}
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((r) => (
                <tr key={r._id}
                    onClick={() => {
                      if (tab === 'inbox')     return setSelectedMessage(r._id);
                      if (tab === 'contacts')  return setSelectedContact(r._id);
                      if (tab === 'campaigns') return setSelectedCampaign(r._id);
                      if (tab === 'accounts')  return setSelectedAccount(r._id);
                      if (tab === 'sequences') return setSelectedSequence(r._id);
                      if (tab === 'templates') return setSelectedTemplate(r);
                      if (tab === 'crmsources') return setSelectedSource(r);
                    }}
                    style={{ cursor: ['inbox','contacts','campaigns','accounts','sequences','templates','crmsources'].includes(tab) ? 'pointer' : 'default' }}>
                  {tab === 'contacts' && (
                    <td onClick={(e) => e.stopPropagation()} style={{ width: 28 }}>
                      <input type="checkbox" checked={selectedIds.has(r._id)} onChange={() => toggleSelect(r._id)} />
                    </td>
                  )}
                  {cols.map(([key]) => {
                    // Synthetic columns: rates computed on the fly so
                    // the campaign table reads as a funnel without
                    // requiring the backend to pre-compute percentages.
                    if (key === '__openRate' || key === '__clickRate') {
                      const sent  = Number((r.stats && r.stats.sent) || 0);
                      const num   = key === '__openRate'
                        ? Number((r.stats && r.stats.opened) || 0)
                        : Number((r.stats && r.stats.clicked) || 0);
                      const pct   = sent > 0 ? Math.round((num / sent) * 1000) / 10 : null;
                      const color = pct == null ? '#888' : pct >= 20 ? '#1e6b3e' : pct >= 5 ? '#856404' : '#a71d2a';
                      return <td key={key} style={{ color, fontWeight: 600 }}>{pct == null ? '—' : pct + '%'}</td>;
                    }
                    if (key === '__stepCount') {
                      return <td key={key}>{Array.isArray(r.steps) ? r.steps.length : 0}</td>;
                    }
                    if (key === '__mailboxes') {
                      const boxes = Array.isArray(r.mailboxes) ? r.mailboxes.filter((m) => m.total > 0) : [];
                      if (!boxes.length) return <td key={key} style={{ color: '#bbb' }}>—</td>;
                      return (
                        <td key={key}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#15172b', marginBottom: 2 }}>{boxes.length} inbox{boxes.length > 1 ? 'es' : ''}</div>
                          {boxes.map((m, ix) => (
                            <div key={ix} title={`${m.address}: ${Number(m.sentEmails).toLocaleString()} sent · ${Number(m.total).toLocaleString()} contacts`}
                                 style={{ fontSize: 10.5, color: '#525252', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 210 }}>
                              <span style={{ color: '#15172b' }}>{m.address}</span> · {Number(m.sentEmails).toLocaleString()} sent
                            </div>
                          ))}
                        </td>
                      );
                    }
                    if (key === '__live') {
                      const active = (r.enrollments && r.enrollments.active) || 0;
                      if (r.isLive) return <td key={key}><span style={{ background: '#e7f5ec', color: '#1e6b3e', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>● live · {active.toLocaleString()}</span></td>;
                      if (r.status === 'approved') return <td key={key}><span style={{ color: '#856404', fontSize: 11 }}>idle</span></td>;
                      return <td key={key} style={{ color: '#bbb', fontSize: 11 }}>—</td>;
                    }
                    if (key === '__progress') {
                      const sent = Number(r.sentEmails || 0);
                      const total = Number(r.totalEmails || 0);
                      if (!total) return <td key={key} style={{ color: '#bbb' }}>—</td>;
                      const pct = Math.min(100, Math.round((sent / total) * 100));
                      return (
                        <td key={key}>
                          <div style={{ fontSize: 11, color: '#525252', marginBottom: 3, whiteSpace: 'nowrap' }}>{sent.toLocaleString()} / {total.toLocaleString()} <span style={{ color: '#888' }}>({pct}%)</span></div>
                          <div style={{ height: 5, background: '#eef2ff', borderRadius: 4, overflow: 'hidden', minWidth: 90 }}>
                            <div style={{ height: '100%', width: pct + '%', background: pct >= 100 ? '#1e6b3e' : '#4b6bfb' }} />
                          </div>
                        </td>
                      );
                    }
                    if (key === '__warmup') {
                      if (!r.warmupStartedAt) return <td key={key} style={{ color: '#888' }}>—</td>;
                      const DAY = 86400000;
                      const day = Math.max(1, Math.floor((Date.now() - new Date(r.warmupStartedAt).getTime()) / DAY) + 1);
                      return <td key={key}><span style={{ background: '#eef2ff', color: '#15172b', padding: '2px 8px', borderRadius: 10, fontSize: 11 }}>day {day}</span></td>;
                    }
                    if (key === '__paused') {
                      return <td key={key}>{r.sendingPaused
                        ? <span style={{ background: '#fdecea', color: '#a71d2a', padding: '2px 8px', borderRadius: 10, fontSize: 11 }}>paused</span>
                        : <span style={{ color: '#1e6b3e', fontSize: 11 }}>✓</span>}</td>;
                    }
                    if (key === '__marketing') {
                      // "off" = excluded from campaigns/sequences (not used by any
                      // sequence as a sender); 1:1 replies still work.
                      return <td key={key}>{r.marketingSenderDisabled
                        ? <span title={r.marketingSenderDisabledReason || 'Not used for marketing / sequences'} style={{ background: '#fdecea', color: '#a71d2a', padding: '2px 8px', borderRadius: 10, fontSize: 11 }}>off · not used by sequences</span>
                        : <span style={{ color: '#1e6b3e', fontSize: 11 }}>on</span>}</td>;
                    }
                    if (key === 'email') return <td key={key}>{fmtCell(getPath(r, key))}<VerifiedTick status={r.sendVetStatus} /></td>;
                    return <td key={key}>{fmtCell(getPath(r, key))}</td>;
                  })}
                  {tab === 'accounts' && (
                    <td onClick={(e) => e.stopPropagation()}>
                      {r.provider === 'm365' ? (
                        r.status === 'connected'
                          ? <span style={{ color: '#1e6b3e', fontSize: 11 }}>✓ connected</span>
                          : <button className="acc-btn acc-btn-primary" style={{ fontSize: 11, padding: '3px 10px' }} onClick={() => connectM365(r._id)}>Connect Microsoft</button>
                      ) : <span style={{ color: '#bbb', fontSize: 11 }}>—</span>}
                    </td>
                  )}
                  {tab === 'contacts' && (
                    <td onClick={(e) => e.stopPropagation()} style={{ position: 'relative', textAlign: 'right' }}>
                      <button className="acc-btn" style={{ fontSize: 14, padding: '0 8px', lineHeight: '20px' }}
                        onClick={() => setOpenMenuId(openMenuId === r._id ? null : r._id)} title="Actions">⋯</button>
                      {openMenuId === r._id && (
                        <>
                          <div style={{ position: 'fixed', inset: 0, zIndex: 39 }} onClick={() => setOpenMenuId(null)} />
                          <div style={{ position: 'absolute', right: 8, top: '100%', zIndex: 40, background: '#fff', border: '1px solid #ddd', borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: 168, overflow: 'hidden' }}>
                            {[
                              ['Edit fields', () => setContactAction({ type: 'edit', ids: [r._id], contact: r })],
                              ['Assign to brand', () => setContactAction({ type: 'assign-brand', ids: [r._id] })],
                              ['Add to list', () => setContactAction({ type: 'assign-list', ids: [r._id] })],
                              [r.unsubscribed ? 'Resubscribe' : 'Unsubscribe', async () => { setOpenMenuId(null); await post('/api/email/contacts/bulk', { ids: [r._id], action: 'unsubscribe', value: !r.unsubscribed }, false); refresh(); }],
                              ['Delete', async () => { setOpenMenuId(null); if (window.confirm('Delete this contact?')) { await post('/api/email/contacts/bulk', { ids: [r._id], action: 'delete' }, false); refresh(); } }],
                            ].map(([label, fn]) => (
                              <button key={label} onClick={() => { if (label === 'Edit fields' || label.startsWith('Assign') || label.startsWith('Add')) setOpenMenuId(null); fn(); }}
                                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', fontSize: 12, border: 'none', background: 'none', cursor: 'pointer', color: label === 'Delete' ? '#a71d2a' : '#15172b' }}
                                onMouseEnter={(e) => e.target.style.background = '#f5f6f8'} onMouseLeave={(e) => e.target.style.background = 'none'}>{label}</button>
                            ))}
                          </div>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {/* Pager — server pages for contacts (unless mid-search), client pages otherwise */}
        {((SERVER_PAGINATED && !q && total > PAGE_SIZE) || (clientPaged && pageCount > 1)) && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '10px 0', fontSize: 12, color: '#525252' }}>
            <button className="acc-btn" disabled={page <= 1 || busy} onClick={() => { setPage((p) => Math.max(1, p - 1)); setSelectedIds(new Set()); }}>‹ Prev</button>
            <span>Page {page} of {SERVER_PAGINATED ? Math.max(1, Math.ceil(total / PAGE_SIZE)) : pageCount}</span>
            <button className="acc-btn" disabled={busy || (SERVER_PAGINATED ? page >= Math.ceil(total / PAGE_SIZE) : page >= pageCount)} onClick={() => { setPage((p) => p + 1); setSelectedIds(new Set()); }}>Next ›</button>
          </div>
        )}
        {q && SERVER_PAGINATED && (
          <div style={{ textAlign: 'center', padding: '8px 0', fontSize: 11, color: '#9ca3af' }}>
            Showing matches for “{q}” — {searchingAll ? 'still searching the full list…' : `${visibleRows.length} found`}
          </div>
        )}
      </div>
    </div>
  );
}
